'use client';

// Экран переписки с человеком (docs/MESSAGES-PLAN.md, М6): лента обоих направлений,
// медиа, кнопки под сообщением, отметки доставки и личный ответ из кабинета.
// Открывается слоем поверх списка (канон шторок NATIVE-SLIDE §11: боковой слой в
// приложении, drawer в вебе — оба даёт useSlidePanel).
// Что можно приложить и какого размера — из свода правил каналов (@scriptera/ui/scenario),
// того же, что читают конструктор и сервер: разъедутся — человек приложит файл, который
// площадка не примет. Окно ответа Инстаграма (24 часа) показываем прямо в шапке ленты и
// при закрытом окне поле ввода не рисуем вовсе.
import {
  cloneElement,
  Fragment,
  isValidElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { Composer, useVoiceInput } from '@scriptera/ui';
import { KBareButton, KButton, KDialog, KInput } from '@scriptera/ui/kit';
import {
  CHAT_ATTACH_KINDS,
  chatAttachLimit,
  type ChatAttachKind,
  type ChatChannelId,
} from '@scriptera/ui/scenario';
import { Link, useRouter } from '@/lib/cabinet-nav';
import { fullNameNote, shortName } from '@/lib/display-name';
import { msgEventAt } from '@/lib/msg-dates';
import { dayKey, fmtMsgDate, fmtMsgTime } from './chat';
import { FFormError } from './form-kit';
import { eventLine } from './messages-events';
import { lastReadAfterFeed, readAgain, unreadToRead } from './messages-read';
import {
  nativeBridge,
  nativeMethod,
  readAccentRgb,
  readBubbleColors,
  type NativeChatAttachAsk,
  type NativeChatInfoData,
} from '@/lib/native-app';
import { anchorTop, feedInset, needOlder } from '@/lib/msg-inset';
import { canWrite, commentReplyOpen, replyWindowShut, windowLeft } from '@/lib/msg-window';
// Форма ленты, сборка альбома и перевод в ряды родной ленты приложения живут ОДНИМ
// модулем (трек Н): показов у переписки два — веб и приложение, — а правда о ней одна.
import {
  capOf,
  failLine,
  feedGroups,
  feedMedia,
  fileKind,
  headOf,
  headOpensIg,
  nativeChatMedia,
  chatPatchReady,
  feedRows,
  mediaFrame,
  msgView,
  msgOwnShot,
  msgShot,
  noFileWord,
  readMarkId,
  replyOf,
  richParts,
  rowButtons,
  statusKey,
  nativeChatPatch,
  nativeChatRows,
  type FeedCard,
  type FeedFile,
  type FeedButton,
  type FeedMedia,
  type FeedMessage,
  type FeedMeta,
  type FeedResp,
  type NativeChatDict,
  type NativeChatHead,
  type NativeChatReply,
  type NativeChatWords,
} from '@/lib/msg-feed';
import { useMessagesLive } from '@/lib/messages-live';
import { usePaneVisible } from '@/lib/pane-visible';
import { usePersistentData } from '@/lib/use-persistent-data';
import { awaitPersistent } from '@/lib/cabinet-warm';
import { warmTarget } from '@/lib/warm';
import { subscribeTopLayer } from '@/lib/native-layers';
import { imgSquarePng } from './avatar';
import { askNativeConfirm, hasNativeConfirm } from './confirm-sheet';
import { LoadError } from './load-error';
import { useIsDark } from './n8n-icon';
import { MediaViewer } from './messages-lightbox';
import { useSlidePanel } from './native-layer-host';
import { Sk } from './skeleton';
import { Twemoji } from './twemoji';
// Импорт компонентов из './messages' образует цикл messages ↔ messages-dialog — он
// безопасен: оба используются только в рендере, к моменту которого модули дозагружены.
import { DialogAvatar, DialogInfoSheet, KIND_KEY, tellMine, type DialogListRow } from './messages';
// КАРТОЧКА СОБЕСЕДНИКА = КАРТОЧКА КОНТАКТА (владелец 09.09.2026): состав строк, слова и
// правку карточки держит ОДНА рука раздела «Контакты» — второго набора слов не заводим.
// Цикл модулей тут такой же и такой же безопасный, как у './messages' выше: рука зовётся
// только в рендере, к моменту которого модуль дозагружен.
import { contactInitial, useContactCardNative } from './contacts';
// Журнал клиента (DIAGNOSTICS-CANON): по жалобе «прочитанное осталось непрочитанным»
// первый ход — журнал, а не догадка.
import { log as journalLog } from '@/lib/client-journal';

/**
 * Сколько едет РОДНОЙ выезд экрана переписки, прежде чем встанет на место (Ф12).
 * Число — из натива (WebScreenTransition.swift, LayerGeom.dur = 0.35 с) плюс запас на
 * кадр: раньше времени звать родное поле нельзя, оно встало бы посреди едущей карточки.
 */
const CHAT_SLIDE_MS = 420;

// ── ЛЕНТА ПЕРЕПИСКИ В КЭШЕ: ОДИН КЛЮЧ, ОДИН АДРЕС, ОДНА МЕРКА ─────────────────────────
//
// Ключ и адрес нужны в двух местах: сама лента (usePersistentData ниже) и ПРОГРЕВ из
// списка переписок (П39 обхода владельца 12.09.2026 — переписка обязана выезжать уже с
// сообщениями). Своей строки ключа у второго места заводить нельзя: другая приставка или
// другой `accept` дали бы ВТОРОЙ кэш той же переписки, и прогрев грел бы мимо.

/** Ключ кэша ленты. `at` — открытие из поиска: страница ВОКРУГ реплики живёт отдельно от
 *  обычного конца переписки (владелец открывает и то, и то). */
export function dialogFeedKey(id: string, at = ''): string {
  return `cabinet:dialog:${id}${at ? `:at:${at}` : ''}`;
}

/** Адрес ленты — тот же, что у ключа выше. */
export function dialogFeedUrl(id: string, at = ''): string {
  return `/api/cabinet/messages/${id}${at ? `?around=${encodeURIComponent(at)}` : ''}`;
}

/** Годный ответ ленты (кэшируем только его). */
const acceptFeed = (raw: unknown): boolean => !!(raw as FeedResp)?.ok;

/**
 * ПРОГРЕТЬ ЛЕНТУ ПЕРЕПИСКИ (П39). Список зовёт это заранее — для видимых рядов (и полэкрана вперёд) и по
 * нажатию на ряд: страница ложится в тот же кэш, и открытая следом переписка берёт её
 * оттуда, а не грузит с нуля уже на экране. Второго пути загрузки ленты нет.
 */
export function warmDialogFeed(id: string, opts: { at?: string; urgent?: boolean; light?: boolean } = {}): void {
  if (!id) return;
  // ЛЁГКАЯ ЛЕНТА (PREFETCH-CAREFUL-TZ §2 п.5, владелец 11.10.2026): фон из списка греет 20 последних сообщений под
  // ТОТ ЖЕ ключ, что читает экран; экран откроется из кэша тем же кадром, а его штатное обновление после открытия
  // (PREFETCH-TZ п.4) принесёт полную страницу. Касание (`urgent`) и страница вокруг находки — полные.
  const light = !!opts.light && !opts.urgent && !opts.at;
  const url = light ? `${dialogFeedUrl(id)}?limit=${DIALOG_FEED_LIGHT}` : dialogFeedUrl(id, opts.at);
  // Цель плана прогрева `chats.feed` (У2): фон — очередью, касание — сразу.
  warmTarget('chats.feed', dialogFeedKey(id, opts.at), url, { accept: acceptFeed }, !!opts.urgent);
}

/** Сколько последних сообщений везёт лёгкий прогрев ленты (PREFETCH-CAREFUL §2 п.5). */
export const DIALOG_FEED_LIGHT = 20;

/** Строка переписки по её id — ЧЕРЕЗ КЭШ ленты (PREFETCH-TZ §2.2: переписка по ссылке, строки в списке нет).
 *  Та же ручка, что у ленты, и тот же ключ: второго кэша (`dialog-row`) не заводим — он был бы дублем. */
export function awaitDialogRow(id: string): Promise<(Partial<DialogListRow> & { id: string }) | null> {
  return awaitPersistent<FeedResp>(dialogFeedKey(id), dialogFeedUrl(id), { accept: acceptFeed }).then(
    (j) => (j?.dialog as (Partial<DialogListRow> & { id: string }) | undefined) ?? null,
  );
}

/**
 * СКОЛЬКО БАЙТ ФАЙЛА РАЗУМНО ПРОВЕЗТИ МОСТОМ (волна 2, Опус-В2Г). Родной выбор отдаёт
 * файл строкой base64 — тем же способом, что снимок мастера анкеты (wizPhoto), — а
 * строка живёт сразу в двух памятях: оболочки и веб-страницы, и растёт против самого
 * файла на треть. Канальные пределы доходят до 25 МБ (Инстаграм, видео) — такую строку
 * возить нельзя. Тяжелее этого числа натив файл не берёт вовсе: он говорит об этом
 * событием, и веб открывает свой прежний выбор файла, где байты никуда не переезжают.
 * Снимков это не касается — их натив уменьшает сам (длинная сторона 1600).
 */
const BRIDGE_FILE_BYTES = 6 * 1024 * 1024;

/** Канал переписки → канал свода правил (там полные имена площадок). */
const rulesChannel = (channel: 'tg' | 'ig'): ChatChannelId =>
  channel === 'tg' ? 'telegram' : 'instagram';

/**
 * ПОЛОСА НАД ЛЕНТОЙ — роль набора `.k-glass-head`: с 05.10.2026 в вебе ГРАДИЕНТ БЕЗ РАЗМЫТИЯ (ТЗ
 * WEB-CAUSES М34/М46 ч.1 — «стекла нет вовсе»): цвет верха у кромки → 0 к ленте, до низа шапки + 24.
 * Низ переписки — сама плашка «Окно ответа закрыто» (`.k-glass-bar`, тот же рецепт зеркально).
 */

/** Полоса-градиент над лентой (под шапкой). */
function GlassStrip() {
  const ref = useRef<HTMLElement>(null);
  // ВЫСОТУ ШАПКИ МЕРЯЕМ ЖИВЬЁМ, А НЕ СЧИТАЕМ ФОРМУЛОЙ. Общее выражение шапки
  // (--ctl-sm + два --ctl-edge = 68) верно только у заголовка в одну строку; у переписки
  // под именем стоит логин, и шапка выше — промер на 390 дал 74,3. Полоса, посчитанная
  // формулой, кончалась бы на 34 ниже шапки вместо 40. Снизу вверх переменные не ходят,
  // поэтому число кладёт сюда сам показ — тем же приёмом, что пропорцию кадра в просмотре
  // (--mvw-ar). Шапки нет вовсе (родная шапка приложения) — полоса и не рисуется.
  useLayoutEffect(() => {
    const strip = ref.current;
    const head = strip?.closest('.drawer, .native-layer')?.querySelector('.dr-head');
    if (!strip || !(head instanceof HTMLElement)) return;
    const sync = () => strip.style.setProperty('--msg-head-h', `${head.offsetHeight}px`);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(head);
    return () => ro.disconnect();
  }, []);
  return (
    <i ref={ref} className="msg-glass msg-glass-top k-glass-head" aria-hidden />
  );
}

/** ПОКАЗЫВАТЬ НЕЧЕГО (29.1): ключ вида → запись словаря кабинета. Слово собирается на
 *  языке клиента живьём, поэтому в базе лежит вид реплики, а не готовая фраза. */
const NOTE_KEY: Record<'ephemeral' | 'unsupported' | 'deleted' | 'pending', string> = {
  ephemeral: 'kindEphemeral',
  unsupported: 'kindUnsupported',
  deleted: 'kindDeleted',
  // Ещё не докачано (О30): в вебе тут заглушка без слов, слово нужно только родным рядам
  // приложения — там заглушки нет до сборки. Оно нейтральное: «Загружается».
  pending: 'kindPending',
};

/** Вид вложения по типу файла: что именно шлём, решаем как мессенджеры — по mime. */
function attachKindOf(mime: string, name: string): ChatAttachKind {
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  // Тип не пришёл (бывает у старых Android-браузеров) — гадаем по расширению картинки.
  if (!mime && /\.(jpe?g|png|gif|webp)$/i.test(name)) return 'image';
  return 'file';
}

// ОКНО ОТВЕТА (закрыто? сколько осталось?) — ОДНА МЕРКА НА ВСЕ ПУТИ, включая саму отправку
// на сервере: она живёт в @/lib/msg-window (О27.4). Здесь только пере-экспорт, чтобы
// карточка собеседника и список переписок звали ровно тот же код, что шапка ленты.
export { canWrite, replyWindowShut, windowLeft };

function fmtBytes(bytes: number, locale: string): string {
  const mb = bytes / (1024 * 1024);
  const n = mb >= 1 ? mb : bytes / 1024;
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(n)} ${mb >= 1 ? 'MB' : 'KB'}`;
}

/** «1:07» — время голосового. */
const mmss = (sec: number): string =>
  `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(2, '0')}`;

/**
 * ДОРОЖКА ГОЛОСОВОГО — 48 СТОЛБИКОВ (реестр §10.3, Б4–Б5; заказ владельца 04.09: «аудио и
 * голосовые выглядят скучно»).
 *
 * Считается ОДИН РАЗ на файл и живёт в памяти вкладки: разбор занимает десятки
 * миллисекунд, а лента перерисовывается каждым тиком опроса — считать заново на каждый
 * кадр нельзя. Сервер волну не считает вовсе (договор §10.4: поле `wave` оставлено на
 * будущее), поэтому её знает тот, кто уже держит файл, — сам показ.
 *
 * Не посчиталась (формат, который браузер не декодирует, — так ведёт себя Safari с
 * opus-голосовыми Телеграма; закрытый AudioContext; сеть) — молчим и рисуем ровную
 * дорожку: человеку важно слушать, а не смотреть на ошибку (запрет №7).
 */
const WAVE_BARS = 48;
const waveCache = new Map<string, number[]>();

function useVoiceWave(url: string): number[] | null {
  const [wave, setWave] = useState<number[] | null>(() => waveCache.get(url) ?? null);
  useEffect(() => {
    if (!url) return;
    const known = waveCache.get(url);
    if (known) {
      setWave(known);
      return;
    }
    let alive = true;
    void (async () => {
      try {
        const Ctx =
          window.AudioContext ??
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctx) return;
        const res = await fetch(url);
        if (!res.ok) return;
        const bytes = await res.arrayBuffer();
        const ctx = new Ctx();
        const audio = await ctx.decodeAudioData(bytes);
        void ctx.close();
        const data = audio.getChannelData(0);
        const step = Math.max(1, Math.floor(data.length / WAVE_BARS));
        const bars: number[] = [];
        for (let i = 0; i < WAVE_BARS; i += 1) {
          let sum = 0;
          const from = i * step;
          for (let j = 0; j < step; j += 1) {
            const v = data[from + j] ?? 0;
            sum += v * v;
          }
          bars.push(Math.sqrt(sum / step));
        }
        const peak = Math.max(...bars, 0.0001);
        const norm = bars.map((v) => Math.min(1, v / peak));
        waveCache.set(url, norm);
        if (alive) setWave(norm);
      } catch {
        /* волну не посчитали — дорожка останется ровной, слушать это не мешает */
      }
    })();
    return () => {
      alive = false;
    };
  }, [url]);
  return wave;
}

/**
 * Голосовое в ленте: своя дорожка вместо системного плеера — в мессенджерах голосовое
 * выглядит одинаково у обеих сторон, а <audio controls> в каждом браузере свой и в пузырь
 * не вписывается. Длительность знаем из самого сообщения (её присылает канал), а если
 * не пришла — берём у файла, когда браузер прочитает заголовок.
 */
function VoicePlayer({
  file,
  playWord,
  pauseWord,
  goneWord,
}: {
  file: FeedFile;
  playWord: string;
  pauseWord: string;
  goneWord: string;
}) {
  const ref = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const [dur, setDur] = useState(file.seconds ?? 0);
  // Волна считается при первом показе и живёт в памяти вкладки (см. useVoiceWave).
  const wave = useVoiceWave(file.url);
  // ФАЙЛ НЕ ЗАГРУЗИЛСЯ (О13): показываем честную строку, а не вечные 0:00 на дорожке.
  // Что именно случилось — не наше клиентское дело (запрет №7): человеку сказано словами,
  // разбираемся мы.
  const [failed, setFailed] = useState(false);

  const toggle = () => {
    const el = ref.current;
    if (!el) return;
    if (el.paused) void el.play().catch(() => setPlaying(false));
    else el.pause();
  };

  // Перемотка щелчком по дорожке — как в любом плеере.
  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el || !dur) return;
    const box = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - box.left) / box.width));
    el.currentTime = ratio * dur;
    setPos(el.currentTime);
  };

  const pct = dur > 0 ? Math.min(100, (pos / dur) * 100) : 0;
  if (failed) return <span className="meta msg-voice-gone">{goneWord}</span>;
  return (
    <div className="msg-voice">
      <KBareButton
        className="msg-voice-btn"
        onClick={toggle}
        aria-label={playing ? pauseWord : playWord}
        title={playing ? pauseWord : playWord}
      >
        {playing ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <rect x="6" y="5" width="4" height="14" rx="1" />
            <rect x="14" y="5" width="4" height="14" rx="1" />
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M8 5.5v13l11-6.5z" />
          </svg>
        )}
      </KBareButton>
      {/* ДОРОЖКА СТОЛБИКАМИ (Б4–Б5 реестра §10.3): 48 полосок, прослушанное закрашено
          цветом стороны. Волна ещё не посчитана (или формат не декодируется) — столбики
          стоят ровно: дорожка остаётся дорожкой, а не пустой полосой. */}
      <div className="msg-voice-wave" onClick={seek}>
        {(wave ?? Array.from({ length: WAVE_BARS }, () => 0.34)).map((v, i) => (
          <span
            key={i}
            className={`msg-voice-bar${(i / WAVE_BARS) * 100 < pct ? ' on' : ''}`}
            style={{ height: `${Math.round(Math.max(0.14, v) * 100)}%` }}
          />
        ))}
      </div>
      <span className="msg-voice-time">{mmss(playing || pos > 0 ? pos : dur)}</span>
      <audio
        ref={ref}
        src={file.url}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onTimeUpdate={(e) => setPos(e.currentTarget.currentTime)}
        onEnded={() => {
          setPlaying(false);
          setPos(0);
        }}
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (Number.isFinite(d) && d > 0) setDur(d);
        }}
        onError={() => {
          setPlaying(false);
          setFailed(true);
        }}
      />
    </div>
  );
}

/**
 * КАДР МЕДИА — ОДИН ЗАКОН НА ВСЮ ПЕРЕПИСКУ (заказ владельца 18.08.2026, Ф17.1): фото,
 * ролик, кадр истории и кадр репоста стоят в ленте ОДНОЙ высоты, ширина — по настоящим
 * пропорциям файла, картинка ЦЕЛИКОМ (обрезки нет), а если по пропорции она вылезает за
 * потолок ширины — уменьшается высота, но не пропорции. Само правило считает общая
 * функция `mediaFrame` (lib/msg-feed.ts), числа живут в cabinet.css.
 *
 * Пропорции берутся из базы (`file_w`/`file_h`); у кого их нет (репосты, старые записи) —
 * узнаём у самого файла событием загрузки и запоминаем: до этого кадр стоит общей высотой
 * и лента не прыгает.
 */
function MediaShot({
  src,
  video = false,
  poster,
  alt,
  className = 'msg-media',
  grid = false,
  shape,
  boxed = false,
  onDims,
}: {
  /** Форма видео (А4): кружок Телеграма крутится в круге, гифка играет сама. */
  shape?: 'circle' | 'gif';
  /** Что показываем (у ролика — сам ролик, у репоста — кадр публикации). */
  src: string;
  video?: boolean;
  poster?: string;
  alt?: string;
  className?: string;
  /** Снимок ВНУТРИ АЛЬБОМА: размер ему даёт сетка пачки, своей коробки у него нет
   *  (иначе пачка из четырёх файлов встала бы лентой в два экрана — владелец 18.08
   *  оставил пачку сеткой). */
  grid?: boolean;
  /** Коробка уже стоит на обёртке — кадр её заполняет (закон Телеграма). */
  boxed?: boolean;
  /** Узнали настоящие размеры у файла: по ним обёртка поставит коробку. */
  onDims?: (w: number, h: number) => void;
}) {
  // КОРОБКУ СТАВИТ КНОПКА-ОБЁРТКА (закон Телеграма, `mediaFrame`): именно она участвует
  // в ширине пузыря, а кадр её заполняет. Пропорции берём из базы; их нет (репост, старая
  // запись) — узнаём у самого файла и сообщаем наверх, чтобы коробка появилась.
  const learn = (nw: number, nh: number) => {
    if (nw > 0 && nh > 0) onDims?.(nw, nh);
  };
  const frame = grid || boxed ? undefined : { height: 'var(--msg-media-h)' as const };
  if (video) {
    return (
      <video
        className={className}
        // КРУЖОК — КРУГЛЫЙ И ОДНОГО РАЗМЕРА (ТЗ CHAT-RENDER-TZ В22): у `video_note`
        // Телеграм не присылает ни ширины, ни высоты — он квадратный по устройству, и
        // общий закон коробки ему не нужен. ГИФКА крутится сама, беззвучно (В23).
        style={shape === 'circle' ? undefined : frame}
        src={src}
        poster={poster}
        muted
        playsInline
        preload="metadata"
        {...(shape === 'gif' ? { autoPlay: true, loop: true } : {})}
        onLoadedMetadata={(e) => learn(e.currentTarget.videoWidth, e.currentTarget.videoHeight)}
      />
    );
  }
  return (
    // eslint-disable-next-line @next/next/no-img-element -- файл из нашего бакета, next/image не нужен
    <img decoding="async"
      className={className}
      style={frame}
      src={src}
      alt={alt ?? ''}
      loading="lazy"
      onLoad={(e) => learn(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)}
    />
  );
}

/**
 * КНОПКА ПОД ПУЗЫРЁМ — ПО ВИДУ ПЛОЩАДКИ (ТЗ CHAT-RENDER-TZ В60–В67, владелец 20.09.2026).
 *
 * У Телеграма девять видов кнопок, и человек видит их по-разному. В ленте кабинета:
 *  • ссылка и мини-приложение — со значком «наружу», нажимаются и открываются;
 *  • «копировать» кладёт текст в буфер прямо здесь;
 *  • нажималка (callback) — только вид: нажать её за клиента мы не вправе;
 *  • погашенная новой клавиатурой (В67) — приглушена: иначе считается живой.
 * Цены на кнопке оплаты не показываем нигде (запрет №7 позиционирования, п.7).
 */
function ChatButton({ button, copied }: { button: FeedButton; copied: string }) {
  const [done, setDone] = useState(false);
  const cls = `msg-btn${button.off ? ' off' : ''}`;
  const mark =
    button.kind === 'url' || button.kind === 'webapp' ? (
      <span className="msg-btn-go" aria-hidden>
        ↗
      </span>
    ) : null;
  if (button.kind === 'copy' && button.copy) {
    return (
      <KBareButton
        className={cls}
        onClick={() => {
          void navigator.clipboard?.writeText(button.copy!).then(
            () => setDone(true),
            () => undefined,
          );
        }}
      >
        {done ? copied : button.label}
      </KBareButton>
    );
  }
  if (button.url) {
    return (
      <a className={cls} href={button.url} target="_blank" rel="noreferrer">
        {button.label}
        {mark}
      </a>
    );
  }
  return <span className={cls}>{button.label}</span>;
}

/**
 * ТЕКСТ С РАЗМЕТКОЙ (ТЗ CHAT-RENDER-TZ В4, владелец 20.09.2026). Жирный, курсив,
 * подчёркнутый, зачёркнутый, код, блок кода, цитата, спойлер и ссылки — то, что человек
 * видит в Телеграме. Раскладку на куски считает общая чистая функция `richParts`
 * (веб и приложение обязаны рисовать одно и то же), здесь — только вид.
 *
 * Разметки нет (старые записи, простой текст) — рисуем как раньше, одним куском.
 */
function RichText({ text, entities }: { text: string; entities?: FeedMeta['entities'] }) {
  const parts = richParts(text, entities);
  if (parts.length === 0) return <Twemoji text={text} />;
  return (
    <>
      {parts.map((p, i) => {
        const cls = p.marks
          .map((mk) => RICH_CLASS[mk])
          .filter(Boolean)
          .join(' ');
        const body = <Twemoji text={p.text} />;
        // Ссылка открывается в новой вкладке: лента переписки места не теряет.
        if (p.url) {
          return (
            <a key={i} className={cls || undefined} href={p.url} target="_blank" rel="noreferrer">
              {body}
            </a>
          );
        }
        return cls ? (
          <span key={i} className={cls}>
            {body}
          </span>
        ) : (
          <Fragment key={i}>{body}</Fragment>
        );
      })}
    </>
  );
}

/**
 * СЛУЖЕБНОЕ СОДЕРЖИМОЕ БЕЗ КАРТОЧКИ (ТЗ CHAT-RENDER-TZ К75): игра, счёт, платное
 * вложение, оплата, возврат, данные мини-приложения. В базе такая строка лежит по-русски
 * (её читают список переписок и пуш), а на экране она обязана быть на языке клиента.
 */
const SYS_KEY: Record<string, string> = {
  game: 'sysGame',
  invoice: 'sysInvoice',
  paid: 'sysPaid',
  payment: 'sysPayment',
  refund: 'sysRefund',
  webapp: 'sysWebApp',
};

/** Вид каждого вида разметки. Имена классов — в cabinet.css рядом с пузырём. */
const RICH_CLASS: Record<string, string> = {
  bold: 'rt-b',
  italic: 'rt-i',
  underline: 'rt-u',
  strikethrough: 'rt-s',
  spoiler: 'rt-sp',
  code: 'rt-code',
  pre: 'rt-pre',
  blockquote: 'rt-q',
  expandable_blockquote: 'rt-q',
};

/**
 * МЕДИА ПОД СПОЙЛЕРОМ (ТЗ CHAT-RENDER-TZ В18, владелец 20.09.2026). Человек спрятал
 * картинку или ролик за пеленой — в Телеграме такое открывается нажатием, и в ленте
 * кабинета правило ТО ЖЕ: иначе клиент увидит спрятанное просто открыв переписку.
 * Открыли — обратно уже не прячем (как у площадки).
 */
function Spoiler({ word, children }: { word: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  if (open) return <>{children}</>;
  return (
    <KBareButton className="msg-spoiler" onClick={() => setOpen(true)} aria-label={word} title={word}>
      <span className="msg-spoiler-in" aria-hidden>
        {children}
      </span>
      <span className="msg-spoiler-cap">{word}</span>
    </KBareButton>
  );
}

/**
 * КАРТОЧКА ФАЙЛА (реестр §10.3, Б6–Б7; заказ владельца 04.09: «файл — просто слово „Файл“,
 * открывается в браузере; нужна карточка файла по типу»).
 *
 * Квадратный значок вида, имя и строка «PDF · 2,4 МБ». Цвет значка — РОЛИ палитры
 * (сигнальный красный у PDF, синий у документа, зелёный у таблицы, янтарный у презентации,
 * приглушённый у архива и прочего): своих красок у карточки нет (CLAUDE.md, «Цвет по
 * ролям»), правило подписано классом вида — сам вид считает общая рука `fileKind`.
 *
 * PDF открывается У НАС, в том же просмотре, что фото и ролики (раздел Е реестра);
 * остальное скачивается ссылкой — показывать содержимое архива нам нечем.
 */
function FileCard({
  file,
  locale,
  words,
  onOpen,
}: {
  file: FeedFile;
  locale: string;
  words: { file: string };
  onOpen: (item: FeedMedia) => void;
}) {
  const type = fileKind(file.mime, file.name);
  const name = (file.name ?? '').trim() || words.file;
  const ext = /\.([a-z0-9]{1,8})$/i.exec(name)?.[1]?.toUpperCase() ?? type.toUpperCase();
  const note = [ext, file.bytes ? fmtBytes(file.bytes, locale) : ''].filter(Boolean).join(' · ');
  const body = (
    <>
      <span className={`msg-file-ic ${type}`} aria-hidden>
        {ext.slice(0, 4)}
      </span>
      <span className="msg-file-col">
        <span className="msg-file-n">{name}</span>
        {note ? <span className="msg-file-m">{note}</span> : null}
      </span>
    </>
  );
  if (type === 'pdf') {
    return (
      <KBareButton
        className="msg-file2"
        onClick={() => onOpen({ kind: 'file', url: file.url, fileType: type, name, title: name })}
        aria-label={name}
        title={name}
      >
        {body}
      </KBareButton>
    );
  }
  return (
    <a className="msg-file2" href={file.url} target="_blank" rel="noreferrer" title={name}>
      {body}
    </a>
  );
}

/**
 * КАРТОЧКА «НА ЧТО ЭТО» — ОДНА ФОРМА НА ВСЕ ДЕВЯТЬ СЛУЧАЕВ (§10.1 п.1 плана CHAT-LOOK,
 * заказ владельца 04.09: «комментарий к рилсу, ответ на историю, ответ на рилс, ответ на
 * публикацию — всё должно быть в ОДНОМ формате: превью-изображение»).
 *
 * ПОРЯДОК СЛОТОВ ПЕРЕВЁРНУТ 04.09 ВЕЧЕРОМ (§11.1 п.2, владелец: «картинка есть — она ВЕРХ
 * пузыря»): превью лежит вплотную к краям пузыря и режется его верхними углами (в том
 * числе углом стыка группы), под ним в полях 14/10 — подпись, ещё ниже — слова реплики
 * (их рисует сам пузырь, не карточка). Отдельной строки-ссылки «Открыть…» больше НЕТ:
 * у публикации и рилса про уход на площадку говорит хвост подписи, а нажимается вся
 * карточка — и превью, и подпись.
 *
 * Куда ведёт нажатие, решает общий msg-feed (headOpensIg): публикация — на площадку
 * ссылкой, история и фото — в наш просмотр. Отдельной вёрстки «под ответ на историю»,
 * «под комментарий» и «под репост» нет ни здесь, ни в Swift.
 */
function HeadCard({
  head,
  href,
  onOpen,
  viewWord,
}: {
  head: NativeChatHead;
  /** Публикация площадки: нажатие уводит по этому адресу, а не в наш просмотр (§11.1 п.4). */
  href?: string;
  onOpen?: () => void;
  viewWord: string;
}) {
  const tall = head.aspect === '9:16';
  /** Нажимаемое место карточки: ссылка на площадку, наш просмотр или просто текст. */
  const press = (cls: string, body: ReactNode): ReactNode =>
    href ? (
      <a className={cls} href={href} target="_blank" rel="noreferrer">
        {body}
      </a>
    ) : onOpen ? (
      <KBareButton className={cls} onClick={onOpen} aria-label={viewWord} title={viewWord}>
        {body}
      </KBareButton>
    ) : (
      <span className={cls}>{body}</span>
    );
  const emoji = head.emoji ? (
    <span className="msg-head-em">
      <Twemoji text={head.emoji} />
    </span>
  ) : null;
  return (
    <span className="msg-head">
      {head.imageUrl
        ? press(
            'msg-head-shot-b',
            <>
              {/* eslint-disable-next-line @next/next/no-img-element -- файл из нашего бакета */}
              <img decoding="async"
                className={`msg-head-shot${tall ? ' tall' : ''}`}
                src={head.imageUrl}
                alt=""
                loading="lazy"
              />
              {emoji}
            </>,
          )
        : head.placeholder
          ? press(
              'msg-head-shot-b',
              <>
                <span className={`msg-head-plug${tall ? ' tall' : ''}`}>{head.placeholder}</span>
                {emoji}
              </>,
            )
          : null}
      {press('msg-head-t', head.label)}
      {head.title ? <span className="msg-head-cap">{head.title}</span> : null}
    </span>
  );
}

/**
 * ЦИТАТА ОТВЕТА — КОМПАКТНАЯ, КАК В ТЕЛЕГРАМЕ (владелец 20.09.2026 по скрину переписки:
 * «в нашем приложении криво отрисована переписка»).
 *
 * Полоса слева цветом своей стороны, имя автора исходного, ОДНА строка текста с обрезкой
 * и, если у процитированного было медиа, маленькая миниатюра 36×36 справа. Полноразмерный
 * кадр у ответа бывает ТОЛЬКО там, где он и есть содержание реплики: ответ на историю и
 * комментарий под публикацией — их рисует карточка «на что это» (HeadCard выше), и решает
 * это общий msg-feed (headSource), а не разметка.
 *
 * Что отменено: ответ на обычное сообщение карточкой «на что это» — он раздувал пузырь в
 * полотно с чужим кадром во всю ширину.
 *
 * Слова и кадр считает общий msg-feed (replyOf) — одна рука на веб-ленту и родную ленту
 * приложения (ChatLayout.replyBlocks), второй правды у цитаты быть не должно.
 */
function QuoteHead({
  reply,
  onOpen,
  viewWord,
}: {
  reply: NativeChatReply;
  onOpen?: () => void;
  viewWord: string;
}) {
  const shot = reply.imageUrl ? (
    /* eslint-disable-next-line @next/next/no-img-element -- файл из нашего бакета */
    <img decoding="async" className="msg-quote-shot" src={reply.imageUrl} alt="" loading="lazy" />
  ) : null;
  return (
    <span className="msg-quote">
      <span className="msg-quote-bar" aria-hidden />
      <span className="msg-quote-t">
        <span className="msg-quote-who">{reply.label}</span>
        {reply.quote ? <span className="msg-quote-x">{reply.quote}</span> : null}
      </span>
      {shot && onOpen ? (
        <KBareButton className="msg-quote-shot-b" onClick={onOpen} aria-label={viewWord} title={viewWord}>
          {shot}
        </KBareButton>
      ) : (
        shot
      )}
    </span>
  );
}

/**
 * КАРТОЧКИ СОДЕРЖИМОГО ТЕЛЕГРАМА — Г1–Г4 РЕЕСТРА §10.3 (заказ владельца 04.09: «старшая
 * модель обязана перечислить ВСЕ типы, дать по каждому указание вида и добиться, чтобы
 * каждый был сделан»).
 *
 * Г1 контакт — круг с инициалами, имя, телефон (кнопка «Написать» — под пузырём);
 * Г2 геопозиция — название, адрес или координаты и ссылка «Открыть на карте» (Apple Maps);
 * Г3 опрос — вопрос, варианты строками с полосой доли и процентом;
 * Г4 кубик — присланный значок крупно и «Выпало N», без пузыря (ряд «одни значки»).
 *
 * Живых данных этих видов в базе пока НЕТ (Телеграм-переписок у клиентов три), и в
 * образцовой переписке «Fable» их тоже нет: придуманных рядов там не бывает (§16.0 п.2,
 * владелец 06.09.2026). Код остаётся — типы настоящие и придут с живыми данными.
 */
function ContentCard({
  card,
  timeSlot,
  words,
}: {
  card: FeedCard;
  /** Время реплики, когда оно едет в последней строке самой карточки (Г1, §14.1 п.1). */
  timeSlot?: ReactNode;
  words: {
    contact: string;
    place: string;
    location: string;
    liveLocation: string;
    openMap: string;
    poll: string;
    quiz: string;
    pollClosed: string;
    votes: (n: number) => string;
    dice: string;
    rolled: (n: number) => string;
  };
}) {
  if (card.type === 'contact') {
    const phone = (card.phone ?? '').trim();
    const name = (card.name ?? '').trim();
    // Инициалы — первые буквы имени и фамилии; имени нет — значок человека не рисуем
    // буквами, ставим точку-заглушку той же формы.
    const initials =
      name
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0] ?? '')
        .join('')
        .toUpperCase() || '·';
    /* КНОПКИ «НАПИСАТЬ» ВНУТРИ КАРТОЧКИ БОЛЬШЕ НЕТ (§14.1 п.1, владелец 05.09): она стоит
       ПОД пузырём вместе с остальными кнопками сообщения — список собирает общий msg-feed
       (rowButtons), а показ только раскладывает его. Внутри карточки остались круг с
       инициалами, имя и телефон. */
    return (
      <span className="msg-card">
        <span className="msg-card-hero">
          <span className="msg-card-av" aria-hidden>
            {initials}
          </span>
          <span className="msg-card-col">
            <span className="msg-card-n">
              {name || words.contact}
              {phone ? null : timeSlot}
            </span>
            {phone ? (
              <span className="msg-card-s">
                {phone}
                {timeSlot}
              </span>
            ) : null}
          </span>
        </span>
      </span>
    );
  }
  if (card.type === 'location') {
    const lat = card.lat ?? 0;
    const lon = card.lon ?? 0;
    /* МЕСТО В ВЕБЕ — СТРОКИ И ССЫЛКА, БЕЗ КАРТЫ (§16.0 п.3, владелец 06.09: «булавка
       размером с картинку — выдумка»). Своей карты в кабинете нет, а плитка-заглушка с
       булавкой не показывала ни улицы, ни точки — только занимала место. Настоящий снимок
       карты рисует РОДНАЯ лента приложения (MapKit), веб уводит в Apple Maps ссылкой,
       адрес которой собирает общий msg-feed (mapUrl). */
    return (
      <span className="msg-card">
        <span className="meta msg-card-t">
          {card.live ? words.liveLocation : card.title ? words.place : words.location}
        </span>
        {card.title ? <span className="msg-card-n">{card.title}</span> : null}
        <span className="msg-card-s">
          {card.address || `${lat.toFixed(5)}, ${lon.toFixed(5)}`}
        </span>
        <a
          className="msg-card-link"
          href={`https://maps.apple.com/?ll=${lat},${lon}&q=${encodeURIComponent(card.title || words.place)}`}
          target="_blank"
          rel="noreferrer"
        >
          {words.openMap}
        </a>
      </span>
    );
  }
  if (card.type === 'poll') {
    const opts = card.options ?? [];
    const total =
      typeof card.votes === 'number' && card.votes > 0
        ? card.votes
        : opts.reduce((sum, o) => sum + (typeof o.votes === 'number' ? o.votes : 0), 0);
    return (
      <span className="msg-card">
        <span className="meta msg-card-t">
          {card.quiz ? words.quiz : words.poll}
          {card.closed ? ` · ${words.pollClosed}` : ''}
        </span>
        {card.question ? <span className="msg-card-n">{card.question}</span> : null}
        <span className="msg-poll">
          {opts.map((o, i) => {
            const share =
              typeof o.votes === 'number' && total > 0 ? Math.round((o.votes / total) * 100) : null;
            return (
              <span key={`${o.text}-${i}`} className="msg-poll-o">
                {/* Полоса доли — фон строки, а не отдельный элемент: так вариант читается
                    целиком, а не «текст и палочка под ним». */}
                {share === null ? null : (
                  <span className="msg-poll-bar" style={{ width: `${share}%` }} aria-hidden />
                )}
                <span className="msg-poll-t">{o.text}</span>
                {share === null ? null : <span className="msg-poll-p">{share} %</span>}
              </span>
            );
          })}
        </span>
      </span>
    );
  }
  /* КУБИК — КАК РЯД ИЗ ОДНИХ ЗНАЧКОВ (§16.0 п.3, владелец 06.09: «кубик значком — выдумка;
     приходит эмодзи и выпавшее число»). Ни карточки, ни пузыря: сам присланный значок
     крупно (кегль ряда `bare`), под ним «Выпало N», ещё ниже — время строкой ряда. Пузырь
     этому ряду снимает класс `.bare` (его ставит сам ряд, см. bareEmoji ниже). */
  return (
    <span className="msg-dice">
      <span className="msg-dice-e">
        <Twemoji text={card.emoji || '🎲'} />
      </span>
      <span className="msg-dice-n">{words.rolled(card.value ?? 0)}</span>
    </span>
  );
}

/** Значок «играть» поверх постера ролика: до нажатия ролик стоит кадром, а не плеером. */
function PlayBadge() {
  return (
    <span className="msg-play" aria-hidden>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M8 5.5v13l11-6.5z" />
      </svg>
    </span>
  );
}

/**
 * Вложение в ленте: картинка/видео/звук показываются на месте, файл — строкой-ссылкой.
 *
 * ВЫСОТА У ВСЕГО МЕДИА ОДНА (--msg-media-h, заказ владельца 18.08.2026): ширина — по
 * настоящим пропорциям файла, без обрезки; потолок ширины — ~2/3 ленты. Закон считает
 * общий mediaFrame (lib/msg-feed.ts), числа живут в cabinet.css, здесь только разметка.
 *
 * ВСЁ ОТКРЫВАЕТСЯ ПО НАЖАТИЮ: фото, ролик и кадр репоста — нашим слоем (`onOpen` →
 * MediaViewer в шторке/панели), голосовое играет прямо в пузыре как раньше, уход на
 * страницу публикации в Инстаграме остаётся подписью репоста.
 */
function Attachment({
  kind,
  file,
  locale,
  words,
  onOpen,
  gone = false,
  grid = false,
  corner,
  shape,
}: {
  kind: string;
  file: FeedFile;
  /** Форма видео (А4): кружок или гифка; не задано — обычный ролик. */
  shape?: 'circle' | 'gif';
  locale: string;
  words: {
    file: string;
    play: string;
    pause: string;
    story: string;
    voiceGone: string;
    viewPhoto: string;
    viewVideo: string;
  };
  /** Открыть медиа полноэкранно (просмотр — наш, не площадки). */
  onOpen: (item: FeedMedia) => void;
  /** Файла у этой реплики больше нет (О13) — плеера не будет, будет честная строка. */
  gone?: boolean;
  /** Снимок в пачке (альбом): размер даёт сетка, общий закон коробки к нему не идёт. */
  grid?: boolean;
  /** ВРЕМЯ КАПСУЛОЙ НА КАДРЕ (§3-v2.6) — рисуется ВНУТРИ кадра, а не в углу пузыря: у
   *  репоста под кадром стоит своя подпись, и капсула из пузыря села бы на неё. */
  corner?: ReactNode;
}) {
  const [learned, setLearned] = useState<{ w: number; h: number } | null>(null);
  const learn = (w: number, h: number) => {
    if (learned?.w !== w || learned?.h !== h) setLearned({ w, h });
  };
  // КОРОБКА КАДРА ПО ЗАКОНУ ТЕЛЕГРАМА (заказ владельца 20.09.2026, исходники
  // Telegram-iOS): своя ширина кадра становится МИНИМАЛЬНОЙ шириной пузыря, сам кадр
  // занимает всю ширину пилюли и за потолок высоты не вылезает. Пропорции берём из базы,
  // а если их там нет (репост, старая запись) — узнаём у самого файла.
  const box = (f: FeedFile): CSSProperties | undefined => {
    if (grid) return undefined;
    const frame = mediaFrame(f.width ?? learned?.w, f.height ?? learned?.h);
    return (frame as CSSProperties) ?? undefined;
  };
  // Фото — кнопка без своего вида: вид даёт сама картинка (KBareButton, канон набора).
  const photo = (f: FeedFile, name?: string, boxed = false) => (
    <KBareButton
      // Коробка по закону Телеграма — только у ФОТО и ВИДЕО реплики. У стикера свой
      // размер (120), у кадра истории — своя карточка: их этот закон не касается.
      className={boxed ? 'msg-media-b msg-box' : 'msg-media-b'}
      style={boxed ? box(f) : undefined}
      onClick={() =>
        onOpen({
          kind: 'image',
          url: f.url,
          ...(f.width && f.height ? { width: f.width, height: f.height } : {}),
        })
      }
      aria-label={words.viewPhoto}
      title={words.viewPhoto}
    >
      <MediaShot src={f.url} alt={name ?? ''} grid={grid} boxed={boxed && !!box(f)} onDims={learn} />
      {corner}
    </KBareButton>
  );
  // Ролик в ленте стоит КАДРОМ со значком «играть», а плеер открывается нашим слоем
  // (заказ владельца 18.08). Постер снят нашим ffmpeg (П4); пока его нет — просим браузер
  // нарисовать первый кадр фрагментом #t=0.1, иначе в ленте серый прямоугольник.
  const clip = (f: FeedFile) => (
    <KBareButton
      className={shape === 'circle' ? 'msg-media-b msg-circle' : 'msg-media-b msg-box'}
      // Кружок круглый своего размера — общий закон коробки ему не идёт (В22).
      style={shape === 'circle' ? undefined : box(f)}
      onClick={() =>
        onOpen({ kind: 'video', url: f.url, ...(f.thumbUrl ? { poster: f.thumbUrl } : {}) })
      }
      aria-label={words.viewVideo}
      title={words.viewVideo}
    >
      <MediaShot
        video
        src={f.thumbUrl ? f.url : `${f.url}#t=0.1`}
        {...(f.thumbUrl ? { poster: f.thumbUrl } : {})}
        grid={grid}
        boxed={shape !== 'circle' && !!box(f)}
        onDims={learn}
        {...(shape ? { shape } : {})}
      />
      {/* Гифка играет сама — значок «играть» на ней лишний и закрывает картинку (В23). */}
      {shape === 'gif' ? <span className="msg-gif-tag">GIF</span> : <PlayBadge />}
      {corner}
    </KBareButton>
  );

  // Голосовое, у которого файл потерян: показываем словами. Плеер, стоящий на 0:00,
  // человеку ничего не объясняет и выглядит поломкой.
  if (gone && (kind === 'voice' || kind === 'audio')) {
    return <span className="meta msg-voice-gone">{words.voiceGone}</span>;
  }
  if (kind === 'story') {
    // Отметка в истории и ответ на историю: медиа настоящее — показываем его, но с
    // подписью, иначе в ленте это неотличимо от обычного фото.
    const media = (file.mime ?? '').startsWith('video/')
      ? clip(file)
      : file.url
        ? photo(file)
        : null;
    return (
      <span className="msg-story">
        <span className="meta msg-story-t">{words.story}</span>
        {media}
      </span>
    );
  }
  // СТИКЕР (ТЗ CHAT-RENDER-TZ В34–В36). Видео-стикер крутится сам, беззвучно и без
  // значка «играть» — как у площадки. Анимированный (Lottie/.tgs) показать нечем:
  // рисуем его обложку, а не карточку файла, — в ленте это был «стикер.webp, 25 КБ».
  if (kind === 'sticker') {
    const mime = file.mime ?? '';
    if (mime.startsWith('video/')) {
      return (
        <span className="msg-media-b">
          <MediaShot video src={file.url} shape="gif" />
        </span>
      );
    }
    if (mime.startsWith('image/')) return photo(file, file.name);
    if (file.thumbUrl) return photo({ ...file, url: file.thumbUrl }, file.name);
    // Ни картинки, ни обложки — остаётся смайл стикера, его рисует сам пузырь словами.
    return null;
  }
  if (kind === 'image') {
    return photo(file, file.name, true);
  }
  if (kind === 'video') {
    return clip(file);
  }
  if (kind === 'voice') {
    return (
      <VoicePlayer
        file={file}
        playWord={words.play}
        pauseWord={words.pause}
        goneWord={words.voiceGone}
      />
    );
  }
  if (kind === 'audio') {
    // Своя дорожка вместо системного <audio controls> (владелец 2026-08-06): системный
    // плеер в каждом браузере свой, в пузырь не вписывается и рядом с голосовым выглядит
    // чужим. Имя записи — строкой над дорожкой, если оно есть.
    return (
      <span className="msg-audio-w">
        {file.name ? <span className="msg-file-name">{file.name}</span> : null}
        <VoicePlayer
          file={file}
          playWord={words.play}
          pauseWord={words.pause}
          goneWord={words.voiceGone}
        />
      </span>
    );
  }
  // ДОКУМЕНТ — КАРТОЧКОЙ ПО ВИДУ (Б6–Б7 реестра §10.3): значок, имя и «PDF · 2,4 МБ»
  // вместо прежнего голого слова «Файл» ссылкой в браузер.
  return <FileCard file={file} locale={locale} words={{ file: words.file }} onOpen={onOpen} />;
}

/**
 * Кто на самом деле прокручивает ленту переписки.
 *
 * INVARIANT(scriptera): прокручиваемого ищем ПО DOM, а не по ширине экрана. Причина
 * (владелец 2026-08-02, «сообщение отскроллено в самое начало»): в раме разворота
 * крутится сама лента, на мобильном САЙТЕ и в приложении — документ (канон
 * SAFE-AREA-SHELL-REBUILD), а в веб-шторке — её тело (`.dr-body`), и «крутим окно» там
 * не делало ничего. null = крутить окно (документ).
 */
function feedScroller(feed: HTMLElement | null, framed: boolean): HTMLElement | null {
  if (framed && feed) return feed;
  let el = feed?.parentElement ?? null;
  while (el && el !== document.body && el !== document.documentElement) {
    const ov = getComputedStyle(el).overflowY;
    if ((ov === 'auto' || ov === 'scroll') && el.scrollHeight - el.clientHeight > 8) return el;
    el = el.parentElement;
  }
  return null;
}

/**
 * ПАМЯТЬ О РОДНОМ ПОЛЕ НА ВРЕМЯ СЕАНСА: сколько низа веб-вьюпорта закрывает поле в покое
 * (`rest`, вместе с полоской home) и над клавиатурой (`over`, полоски под ней нет).
 * Живёт вне компонента нарочно: числа не зависят от переписки, а перемерять их в каждой
 * открытой заново — значит каждый раз ловить один и тот же перелёт первой клавиатуры
 * (владелец 2026-08-07). Учит эти числа только свежий замер натива — см. feedInset.
 */
const composerMemo = { rest: 0, over: 0 };


/** Переводчик раздела «Чаты» — им говорят и лента, и письмо рассылки в отчёте. */
type ChatT = ReturnType<typeof useTranslations<'cabinet.messages'>>;

/** Всё, что нужно показу одного сообщения ленты (`renderFeedMessage`) от хозяина ленты. */
export interface FeedBubbleCtx {
  t: ChatT;
  locale: string;
  /** Реплика с отметкой «просмотрено» (msg-feed `readMarkId`); пусто — ни одной. */
  markId: string;
  /** Открыть кадр/ролик в просмотре хозяина ленты. */
  openMedia: (item: FeedMedia) => void;
  /** Имя бота/цепочки по id черновика — подпись под нашим сообщением. */
  scenarioName: (id: unknown) => string;
  /** Слова ленты с относительными адресами (веб-показ). */
  webWords: NativeChatWords;
}

/** ПРОСМОТР МЕДИА ЛЕНТЫ нашим слоем (Ф17.3): кадр ищется в списке медиа ленты по адресу — нашли, листается вся лента,
 *  нет — открывается один. Одна рука на переписку и письмо рассылки в отчёте (Р2 ред. 7). */
export function openFeedMedia(viewPanel: ReturnType<typeof useSlidePanel>, t: ChatT, media: FeedMedia[], item: FeedMedia): void {
  const at = media.findIndex((x) => x.url === item.url);
  // ТЕЛЕФОН В БРАУЗЕРЕ (ТЗ WEB-PARITY-3 Ч26, кадр iOS C08): лист без шапки, медиа от верха,
  // тёмный кружок «×» в углу самого просмотра.
  const phoneWeb =
    typeof window !== 'undefined' &&
    !document.documentElement.classList.contains('app') &&
    window.matchMedia('(max-width: 820px)').matches;
  viewPanel.open({
    title: t('viewer'),
    sheet: true,
    // Ручки над кадром нет (ChatViewerSheet: prefersGrabberVisible = false, кадр C08) —
    // уход вниз свайпом от этого не меняется.
    ...(phoneWeb ? { headless: true } : {}),
    body: (
      <MediaViewer
        {...(phoneWeb ? { onClose: () => viewPanel.back() } : {})}
        items={at >= 0 ? media : [item]}
        index={at >= 0 ? at : 0}
        words={{
          prev: t('viewPrev'),
          next: t('viewNext'),
          openIg: t('openInInstagram'),
          download: t('viewDownload'),
        }}
      />
    ),
  });
}

/**
 * ЛЕНТА СООБЩЕНИЙ БЕЗ ПЕРЕПИСКИ — тот же показ реплик, что «Чаты» (`renderFeedMessage`: пузырь, вложения, время, кнопки,
 * подпись под пузырём), только без поля ввода и подгрузки. Им отчёт рассылки показывает своё письмо (APP-CLAIMS-1020
 * ред. 7, Р2): «как его увидит человек». `names` — имена ботов по id черновика для подписи «Бот · <имя>».
 */
export function FeedBubbles({ messages, names, className }: { messages: FeedMessage[]; names?: Record<string, string>; className?: string }) {
  const t = useTranslations('cabinet.messages');
  const locale = useLocale();
  const viewPanel = useSlidePanel();
  const scenarioName = useCallback((id: unknown): string => (typeof id === 'string' && id ? (names?.[id] ?? '') : ''), [names]);
  const words = useChatWords(t, locale, scenarioName);
  const webWords = useMemo<NativeChatWords>(() => ({ ...words, origin: '' }), [words]);
  const rows = feedRows(messages);
  const groups = feedGroups(rows, dayKey);
  const media = feedMedia(messages, { story: t('kindStory'), storyReply: t('replyToStory') });
  const ctx: FeedBubbleCtx = {
    t,
    locale,
    markId: readMarkId(messages),
    openMedia: (item) => openFeedMedia(viewPanel, t, media, item),
    scenarioName,
    webWords,
  };
  return (
    <div className={`msg-feed${className ? ` ${className}` : ''}`}>
      {rows.map(({ m, album }, idx) => (
        <Fragment key={m.id}>{renderFeedMessage(ctx, m, album, groups[idx])}</Fragment>
      ))}
    </div>
  );
}

/** Все слова ленты — из словаря кабинета; меняется язык — приезжают заново. Один набор на переписку и на письмо
 *  рассылки в отчёте (APP-CLAIMS-1020 ред. 7, Р2). */
export function useChatWords(t: ChatT, locale: string, scenarioName: (id: unknown) => string): NativeChatWords {
  return useMemo<NativeChatWords>(
    () => ({
      origin: typeof window === 'undefined' ? '' : window.location.origin,
      time: (at) => fmtMsgTime(at, locale),
      date: (at) => fmtMsgDate(at, locale),
      dayKey: (at) => dayKey(at),
      // Год у даты — общим правилом раздела (msgEventAt): родная лента получает подпись
      // события уже готовой строкой, своего форматирования дат в нативе нет.
      eventAt: (at) => msgEventAt(at, locale),
      eventLine: (m) => eventLine(m, t, scenarioName),
      byBot: (id) => {
        const name = scenarioName(id);
        return name ? t('byBot', { name }) : '';
      },
      // Подпись сообщения цепочки (К15 П9 п.5) — тем же видом, что «Бот · <сценарий>».
      byChain: (id) => {
        const name = scenarioName(id);
        return name ? t('byChain', { name }) : '';
      },
      byScenario: t('byScenario'),
      byOwner: t('byOwner'),
      sending: t('sending'),
      notSent: t('notSent'),
      // ПОЧЕМУ не ушло — словами кабинета, по ключу общего разбора (msg-feed.failKey):
      // сырой отказ площадки клиенту не показывается (запрет №7).
      failWord: (key: string) => (key ? t(key) : ''),
      readMark: t('readMark'),
      edited: t('editedMark'),
      forwardedFrom: (who) => t('forwardedFrom', { who }),
      forwardedHidden: t('forwardedHidden'),
      replyToStory: t('replyToStory'),
      mentionInStory: t('mentionedInStory'),
      storyGone: t('storyUnavailable'),
      reactionToStory: t('reactionToStory'),
      reactionTo: t('reactionTo'),
      tapped: t('tapped'),
      spoilerShow: t('spoilerShow'),
      viaBot: (name: string) => t('viaBot', { name }),
      sysWord: (key: string) => (SYS_KEY[key] ? t(SYS_KEY[key]) : ''),
      kbReply: (list: string) => t('kbReply', { list }),
      kbRemoved: t('kbRemoved'),
      kbForce: t('kbForce'),
      replyToOurs: t('replyToOurs'),
      replyToTheirs: t('replyToTheirs'),
      replyToMessage: t('replyToMessage'),
      kindWord: (kind) => (KIND_KEY[kind] ? t(KIND_KEY[kind]) : ''),
      underPost: t('underPost'),
      underReel: t('underReel'),
      // Хвост подписи карточки публикации: «Рилс · откроется в Инстаграме» (§11.1 п.2).
      opensIg: t('opensInInstagram'),
      openInChannel: t('openInChannel'),
      openIg: t('openInInstagram'),
      post: t('kindShare'),
      reel: t('kindReel'),
      story: t('kindStory'),
      file: t('kindFile'),
      postGone: t('postUnavailable'),
      fileGone: t('fileUnavailable'),
      voiceGone: t('voiceGone'),
      note: (key) => t(NOTE_KEY[key]),
      bytes: (n) => fmtBytes(n, locale),
      cardContact: t('cardContact'),
      cardPlace: t('cardPlace'),
      cardLocation: t('cardLocation'),
      cardLiveLocation: t('cardLiveLocation'),
      cardOpenMap: t('cardOpenMap'),
      cardPoll: t('cardPoll'),
      cardQuiz: t('cardQuiz'),
      cardPollClosed: t('cardPollClosed'),
      cardDice: t('cardDice'),
      cardWrite: t('cardWrite'),
      cardRolled: (n) => t('cardRolled', { n }),
    }),
    [t, locale, scenarioName],
  );
}

/**
 * ОДНО СООБЩЕНИЕ ЛЕНТЫ — ЧЕТЫРЕ СЛОТА, А НЕ ВЁРСТКА ПОД КАЖДЫЙ ВИД (паспорт §3.3,
 * docs/CHAT-LOOK-PLAN.md; заказ владельца 04.09.2026 «нужен общий подход ко всем
 * сочетаниям»). Порядок сверху вниз ОДИН для любого сочетания:
 *   1) подпись НАД пузырём — только у реакции на историю;
 *   2) шапка ВНУТРИ пузыря — «переслано от…» ИЛИ «на что ответ» (история / цитата /
 *      комментарий), больше одной шапки не бывает;
 *   3) тело — карточка → вложения → текст → заметка «показывать нечего»;
 *   4) низ — ряды кнопок, реакции на кромке, время ВНУТРИ пузыря, подпись под ним.
 * Сочетание = какие слоты заполнены. Отдельной вёрстки «под тип» здесь нет и не заводить.
 *
 * Классы — те же, что у чата ассистента (.chat-msg/.chat-bubble/.chat-meta);
 * `album` — вся пачка одного альбома (С6), у обычного сообщения это оно само;
 * `group` — место реплики в склеенной группе (§3.2), считает общий msg-feed.
 */
export function renderFeedMessage(
  ctx: FeedBubbleCtx,
  m: FeedMessage,
  album: FeedMessage[] = [m],
  group?: { first: boolean; last: boolean },
) {
  const { t, locale, markId, openMedia, scenarioName, webWords } = ctx;
  // СОБЫТИЕ (М11.4): не реплика, а строка про отношения — по центру ленты, серым,
  // с датой (как лента лида в CRM). Пузырём его рисовать нельзя: разговор читается
  // сторонами, а событие не принадлежит ни одной из них.
  if (m.direction === 'event') {
    const line = eventLine(m, t, scenarioName);
    if (!line) return null;
    return (
      <div className="msg-ev">
        <span className="msg-ev-line">{line}</span>
        <span className="msg-ev-at">{msgEventAt(m.at, locale)}</span>
      </div>
    );
  }
  // КАК ЭТО ПОКАЗАТЬ — общий ответ (msgView, О27.1.2): у веб-ленты и родной ленты
  // приложения он ОДИН, своих правил вида здесь больше нет.
  const view = msgView(m, album, markId);
  const bodyText = view.text;
  const attWords = {
    file: t('kindFile'),
    play: t('voicePlay'),
    pause: t('voicePause'),
    story: t('kindStory'),
    voiceGone: t('voiceGone'),
    viewPhoto: t('viewPhoto'),
    viewVideo: t('viewVideo'),
  };
  // КАРТОЧКА «НА ЧТО ЭТО» — ОДНА ФОРМА НА ВСЕ ДЕВЯТЬ СЛУЧАЕВ (§10.1 п.1, реестр В1–В8).
  // Что рисовать, целиком решает общий msg-feed (headOf): показ только раскладывает
  // готовые слоты. Прежние три ветки (кадр истории снаружи пузыря, шапка комментария,
  // плитка репоста) этим ОТМЕНЕНЫ — «один формат тут, другой там» и был главной
  // претензией владельца 04.09.
  // Адреса берём ОТНОСИТЕЛЬНЫМИ (origin пустой): по ним просмотр ищет кадр в списке
  // медиа переписки, а он собран такими же — иначе откроется одна картинка вместо всей
  // переписки.
  const headCard = headOf(m, view, webWords);
  const { bareEmoji: bareOnly, mediaOnly } = view;
  // КУБИК ЖИВЁТ РЯДОМ ИЗ ОДНИХ ЗНАЧКОВ (§16.0 п.3, владелец 06.09): присланный значок
  // крупно, под ним «Выпало N» и время — пузырь такому ряду не нужен. Остальные карточки
  // (контакт, место, опрос) пузырь ТРЕБУЮТ: он держит подпись, строки и ссылку вместе.
  const diceCard = m.meta?.card?.type === 'dice';
  // Одни значки без пузыря — только там, где карточки нет: у карточки пузырь держит
  // подпись, превью и ссылку вместе.
  const bareEmoji = (bareOnly || diceCard) && !headCard;
  // Реакция, которой не на чем висеть (значок без истории): подпись остаётся НАД
  // пузырём — превью у неё нет и быть не может.
  const asShot = view.asShot && !headCard;
  // ПОДПИСЬ НАД ПУЗЫРЁМ — по общему правилу msg-feed (capOf, §12.1 п.2): есть карточка
  // «на что это» — строки сверху нет вовсе, её слово уже стоит внутри карточки под
  // картинкой. Своего условия показ больше не держит.
  const capKey = capOf(m, view);
  const cap = capKey ? t(capKey) : '';
  const hasReacts = (m.reactions?.length ?? 0) > 0;
  // ВРЕМЯ ВНУТРИ ПУЗЫРЯ (§3.2), три случая одного правила: капсулой поверх кадра, если
  // у ряда есть кадр; «дыркой» в последней строке текста, чтобы оно обтекалось словами;
  // отдельной строкой справа, когда обтекать нечего (кнопки, карточка, одни значки).
  const timeText = fmtMsgTime(m.at, locale);
  // КАПСУЛА НА КАДРЕ — ТОЛЬКО У ПУЗЫРЯ ИЗ ОДНОЙ ФОТОГРАФИИ ИЛИ РОЛИКА БЕЗ СЛОВ (§11.1
  // п.3, владелец 04.09 вечером): на превью карточки «на что это» время читалось как
  // длительность ролика — там оно ушло в пилюлю пузыря. Правило считает общий msg-feed
  // (msgShot), а не показ.
  const timeCap = album.length > 1 ? album.some((x) => msgShot(x)) : msgShot(m, view);
  const ownShot = album.length <= 1 && msgOwnShot(m, view);
  // КНОПКИ РЯДА — общий ответ msg-feed (rowButtons): слова самого сообщения плюс
  // действие карточки контакта («Написать» стоит ПОД пузырём, а не внутри, §14.1 п.1).
  const btns = view.note === 'deleted' ? [] : rowButtons(m, t('cardWrite'));
  // СПОЙЛЕР (ТЗ CHAT-RENDER-TZ В18): человек спрятал медиа за пеленой, и в Телеграме
  // оно открывается нажатием. В ленте кабинета правило то же — иначе клиент увидит то,
  // что собеседник прятал, просто открыв переписку.
  // ПАЧКА ИЗ ОДНИХ КАДРОВ — СЕТКОЙ, остальное списком карточек (К50).
  const allShots = album.length > 1 && album.every((x) => msgShot(x));
  const veil = (node: ReactNode): ReactNode =>
    m.meta?.spoiler ? <Spoiler word={t('spoilerShow')}>{node}</Spoiler> : node;
  // ВРЕМЯ В ПОСЛЕДНЕЙ СТРОКЕ КАРТОЧКИ КОНТАКТА (§14.1 п.1, владелец 05.09: «время — в
  // последней строке телефона/имени, без своей строки»). Тот же приём, что у слов:
  // невидимая дырка держит место в конце строки, а само время стоит в углу — только
  // якорь у него не пузырь, а сама карточка (у неё свои поля 10/8).
  const cardTime = !timeCap && m.meta?.card?.type === 'contact';
  const timeHole =
    cardTime ||
    (!timeCap && !!bodyText && !view.tap && !m.meta?.card && !bareEmoji && btns.length === 0);
  // Судьба реплики: слово общего msg-feed, у отказа — с причиной СЛОВАМИ (§3.2).
  const stKey = statusKey(m, view.readMark);
  const stText =
    stKey === 'notSent'
      ? failLine(m, t('notSent'), (key: string) => (key ? t(key) : ''))
      : stKey
        ? t(stKey)
        : '';
  const time = (
    <span className={`msg-time-in${timeCap ? ' cap' : ''}${timeHole ? ' hole' : ''}`}>
      {timeText}
    </span>
  );
  // КУДА ВЕДЁТ НАЖАТИЕ ПО КАРТОЧКЕ (§11.1 п.4): публикация площадки — сразу в Инстаграм
  // (в вебе это ссылка в новой вкладке), история и фото — в наш просмотр. Правило
  // считает общий msg-feed (headOpensIg), а не разметка.
  const headIg = headOpensIg(m);
  // Кадр карточки «на что это» открывается у нас и листается со всем медиа переписки.
  const openHead = () => {
    if (headCard?.imageUrl) openMedia({ kind: 'image', url: headCard.imageUrl });
  };
  // РЕПОСТ ЖИВЁТ В КАРТОЧКЕ ЦЕЛИКОМ: своего вложения у него в теле больше нет.
  const shareInHead =
    !!headCard &&
    (m.kind === 'share' || (m.kind === 'system' && (m.file?.mime ?? '').startsWith('text/html')));
  const head = headCard ? (
    <HeadCard
      head={headCard}
      {...(headIg && headCard.linkUrl ? { href: headCard.linkUrl } : {})}
      {...(!headIg && headCard.imageUrl ? { onOpen: openHead } : {})}
      viewWord={t('viewPhoto')}
    />
  ) : m.meta?.forwardFrom || m.meta?.forwardHidden ? (
    /* ПЕРЕСЛАННОЕ (В9 реестра §10.3): в мессенджерах первым делом видно, чьи это слова.
       Без подписи чужое сообщение читалось как сказанное самим собеседником. Автор
       бывает скрыт настройками приватности — тогда честно говорим об этом. */
    <span className="msg-fwd">
      {m.meta.forwardFrom
        ? t('forwardedFrom', { who: m.meta.forwardFrom })
        : t('forwardedHidden')}
    </span>
  ) : null;
  // ЦИТАТА ОТВЕТА — КОМПАКТНАЯ (владелец 20.09.2026): ответ на обычное сообщение больше
  // не карточка «на что это» (msg-feed, headSource), а полоска с именем, строкой текста
  // и миниатюрой 36×36. Слова и кадр считает общий msg-feed (replyOf) — тот же ответ,
  // что уезжает в родную ленту приложения. У реакции своя подпись над пузырём (capOf), у
  // нажатия кнопки цитаты нет вовсе.
  const quoteHead =
    m.replyTo?.kind === 'message' && !headCard && !view.tap && !view.reaction
      ? replyOf(m.replyTo, webWords, false, msgOwnShot(m, view))
      : null;
  const openQuote = () => {
    if (quoteHead?.imageUrl) openMedia({ kind: 'image', url: quoteHead.imageUrl });
  };
  // НАЖАТИЕ КНОПКИ — САМА КНОПКА (владелец 20.09.2026): капсулой становится только ЧИСТОЕ
  // нажатие — у ряда нет ни вложений, ни карточки, ни цитаты, иначе им негде было бы
  // уместиться, и ряд остаётся обычным пузырём. Условие то же, что в родной ленте
  // (ChatLayout, tapPill): вид у обоих показов обязан быть один.
  // Подписи кнопки может не остаться (старая запись, поправленная клавиатура) — слова
  // капсуле даёт словарь («Нажал кнопку»), ровно как их даёт родной ленте msg-feed.
  const tapPill =
    view.tap &&
    !m.file &&
    album.length <= 1 &&
    !m.meta?.card &&
    !headCard &&
    !view.note &&
    !view.skeleton &&
    !view.noFile;
  return (
    <div
      // МЕДИА БЕЗ ПУЗЫРЯ (Ф17.2): в реплике нет ничего, кроме кадра — подложка вокруг
      // него лишняя, углы скругляет само медиа. Кто такой, решает общий msgView.
      // grp-first/grp-last — склейка группы (§3.2): углы стыка режет CSS.
      className={`chat-msg ${m.direction === 'out' ? 'user' : 'peer'}${asShot ? ' shot' : ''}${
        bareEmoji ? ' bare' : ''
      }${mediaOnly ? ' media' : ''}${view.sticker ? ' sticker' : ''}${
        hasReacts ? ' reacted' : ''
      }${/* Нажатие кнопки — капсула-кнопка вместо пузыря (владелец 20.09.2026). */ ''}${
        tapPill ? ' tap' : ''
      }${group?.first === false ? '' : ' grp-first'}${
        group?.last === false ? '' : ' grp-last'
      }`}
    >
      {cap ? <span className="msg-cap">{cap}</span> : null}
      {/* ПОДПИСЬ НАД МЕДИА (ТЗ CHAT-RENDER-TZ В17): у Телеграма это отдельный вид
          сообщения (`show_caption_above_media`) — слова стоят ПЕРЕД картинкой. Слоты
          пузыря те же, меняется только их порядок. */}
      <div className={`chat-bubble msg-bubble${m.meta?.captionAbove ? ' cap-above' : ''}`}>
        {head}
        {/* ЦИТАТА ОТВЕТА (§0.1: слот «на что ответ» стоит там же, где карточка «на что
            это», — вместе они не встречаются никогда). */}
        {quoteHead ? (
          <QuoteHead
            reply={quoteHead}
            {...(quoteHead.imageUrl ? { onOpen: openQuote } : {})}
            viewWord={t('viewPhoto')}
          />
        ) : null}
        {/* КАРТОЧКА СОДЕРЖИМОГО (С6): контакт с телефоном, место с адресом и картой,
            опрос с ответами — вместо строки «Прислал контакт». */}
        {m.meta?.card && (
          <ContentCard
            card={m.meta.card}
            /* Время у карточки контакта живёт ВНУТРИ неё, в последней строке (§14.1 п.1);
               у остальных карточек оно остаётся строкой пузыря, как было. */
            timeSlot={
              cardTime ? (
                <>
                  <span className="msg-time-hole" aria-hidden>
                    {timeText}
                  </span>
                  {time}
                </>
              ) : null
            }
            words={{
              contact: t('cardContact'),
              place: t('cardPlace'),
              location: t('cardLocation'),
              liveLocation: t('cardLiveLocation'),
              openMap: t('cardOpenMap'),
              poll: t('cardPoll'),
              quiz: t('cardQuiz'),
              pollClosed: t('cardPollClosed'),
              votes: (n: number) => t('cardVotes', { n }),
              dice: t('cardDice'),
              rolled: (n: number) => t('cardRolled', { n }),
            }}
          />
        )}
        {/* ПОКАЗЫВАТЬ НЕЧЕГО (29.1): человек удалил сообщение, площадка не отдала
            вложение, исчезающее фото. Вместо пустого пузыря — честная строка словами;
            решение общее с приложением (msgView.note). */}
        {view.note ? (
          <span className={`meta msg-note${view.note === 'deleted' ? ' del' : ''}`}>
            {t(NOTE_KEY[view.note])}
          </span>
        ) : view.skeleton ? (
          /* ЕЩЁ НЕ ДОКАЧАНО (О30, приказ владельца 17.08 «избегай негатива типа „не
             пришло“»): вложение этой реплики закачка ещё добирает. Показываем заглушку
             пузыря — без единого слова; придёт содержимое, и она сменится сама. */
          <span className="msg-skeleton" aria-hidden />
        ) : view.noFile ? (
          /* ВИД ЕСТЬ, ФАЙЛА НЕТ (владелец 20.09.2026: «ниже вообще ничего не
             отрисовано»): реплика записана роликом, записью или документом, а адреса
             файла у неё нет. Пустого пузыря с одним временем быть не должно — вместо
             содержимого стоит плашка ВИДА теми же словами, что в списке переписок.
             Решение общее с приложением (msgView.noFile). */
          <span className="meta msg-note">{noFileWord(m.kind, webWords)}</span>
        ) : album.length > 1 ? (
          veil(
          // ПАЧКА ДОКУМЕНТОВ И ЗАПИСЕЙ — СПИСКОМ, А НЕ СЕТКОЙ (ТЗ CHAT-RENDER-TZ К50):
          // сетка сделана под кадры, и карточки файлов в её клетках расползались.
          // Смешанную пачку (кадры + файл) показываем тоже списком: ровный ряд карточек
          // честнее, чем клетка с обрезанным именем файла.
          <span className={allShots ? 'msg-album' : 'msg-album-list'}>
            {album
              .filter((x) => x.file)
              .map((x) => (
                <Attachment
                  key={x.id}
                  kind={x.kind}
                  file={x.file!}
                  locale={locale}
                  words={attWords}
                  onOpen={openMedia}
                  gone={x.meta?.fileGone}
                  /* Клетку сетки размер получает от неё самой; карточке файла в списке
                     коробка не нужна — она занимает ширину пузыря. */
                  grid={allShots}
                  {...(x.meta?.shape ? { shape: x.meta.shape } : {})}
                />
              ))}
          </span>,
          )
        ) : (
          /* История рисуется даже без файла (у истории Телеграма адреса нет вовсе).
             РЕПОСТ сюда больше не попадает: его кадр, заголовок и ссылка живут в
             карточке «на что это» (§10.1 п.1) — второй плитки у него нет. */
          !shareInHead &&
          (m.file || m.kind === 'story') &&
          veil(
            <Attachment
              kind={m.kind}
              file={m.file ?? { url: '' }}
              locale={locale}
              words={attWords}
              onOpen={openMedia}
              gone={m.meta?.fileGone}
              /* Форма видео (А4): кружок Телеграма круглый, гифка крутится сама. */
              {...(m.meta?.shape ? { shape: m.meta.shape } : {})}
              /* Время — капсулой НА кадре (§3-v2.6): у одиночного вложения она живёт
                 внутри самого кадра, у пачки-альбома остаётся в углу пузыря. */
              corner={timeCap && ownShot ? time : null}
            />,
          )
        )}
        {/* НАЖАТИЕ КНОПКИ — САМА КНОПКА (владелец 20.09.2026: «Пост „Да, хочу“ — это
            кнопка должна быть»). Человек не писал этих слов, он нажал кнопку бота
            (callback Телеграма, быстрый ответ Инстаграма) — и в ленте это та же капсула,
            что у кнопок под пузырём бота (ТЗ CHAT-RENDER-TZ К4), только на его стороне.
            ЧТО ОТМЕНЕНО: пузырь со значком пальца (hand.tap) и цитатой того сообщения,
            чью кнопку нажали, — цитата у нажатия не показывается вовсе (оно и так стоит
            сразу под сообщением с кнопками), а значок владелец назвал кривым.
            У карточки текст — служебная подпись («Прислал контакт»): её место заняла
            сама карточка. У реакции текст — это и есть значок, он уже нарисован крупно
            поверх кадра. */}
        {m.meta?.card || headCard?.emoji ? null : tapPill ? (
          <span className="msg-tap">
            {/* Подписи кнопки может не быть: нажали кнопку из старой переписки, или
                клавиатуру с тех пор поправили. Тогда говорим словами, ЧТО произошло —
                техническую начинку нажатия клиенту не показываем (запрет №7). */}
            <Twemoji text={bodyText || t('tapped')} />
          </span>
        ) : bodyText ? (
          <div className="msg-text">
            {/* ЗНАЧКИ — КАРТИНКАМИ TWEMOJI (§10.1а п.13): системный значок рисует ОС, и
                на Windows и Android он не тот, что видел клиент в Инстаграме. Текст в
                базе не меняется — только отрисовка (components/cabinet/twemoji.tsx).
                РАЗМЕТКА (ТЗ CHAT-RENDER-TZ В4): жирный, курсив, ссылка, код и цитата
                приходят отдельным списком и накладываются на тот же текст. */}
            <RichText text={m.meta?.sys ? t(SYS_KEY[m.meta.sys]) : bodyText} entities={m.meta?.entities} />
            {/* ДЫРКА ПОД ВРЕМЯ: невидимая копия времени в конце последней строки —
                слова обтекают его, как в Телеграме (короткая строка — время рядом,
                длинная — время отдельной строкой справа). */}
            {timeHole ? (
              <span className="msg-time-hole" aria-hidden>
                {timeText}
              </span>
            ) : null}
          </div>
        ) : null}
        {/* ВРЕМЯ (§3.2): внутри пузыря справа-снизу. Капсула у ОДИНОЧНОГО кадра рисуется
            внутри самого кадра (corner выше) — сюда она не попадает; у пачки-альбома
            кадра-хозяина нет, поэтому капсула садится в угол пузыря, а он у пачки и есть
            её нижний правый кадр. */}
        {cardTime ? null : !timeCap || album.length > 1 ? time : null}
        {/* РЕАКЦИЯ (С2): значок висит НА пузыре, как в Телеграме и Инстаграме, а не
            строкой в ленте — строкой она забивала бы разговор. Снятие реакции убирает
            значок само: канал присылает состояние, а не разницу. */}
        {hasReacts && (
          <span className="msg-reacts">
            {m.reactions!.map((r, i) => (
              <span key={`${r.emoji}-${i}`} className="msg-react">
                <Twemoji text={r.emoji} />
              </span>
            ))}
          </span>
        )}
      </div>
      {/* КНОПКИ — ПОД ПУЗЫРЁМ, КАК В ТЕЛЕГРАМЕ (§10.1 п.2, заказ владельца 04.09:
          «конструкция „сообщение, время, кнопки“ странная: в Телеге сообщение и время,
          а кнопки под этим»). Это ОТМЕНЯЕТ капсулы внутри полей пузыря (§3-v2.5).
          Кнопка с адресом нажимается и открывает ссылку, без адреса — только вид: это
          отпечаток сообщения, нажать его можно было в самом канале. */}
      {btns.length > 0 ? (
        <div className="msg-btns">
          {btns.map((b, i) => (
            <ChatButton key={`${b.label}-${i}`} button={b} copied={t('copied')} />
          ))}
        </div>
      ) : null}
      {/* ОБЫЧНАЯ КЛАВИАТУРА — НЕ КНОПКИ СООБЩЕНИЯ (ТЗ CHAT-RENDER-TZ В3, В69).
          `ReplyKeyboardMarkup` заменяет человеку клавиатуру под полем ввода, и рисовать
          её блоком под пузырём нельзя: клиент спутал бы её с кнопками бота. Поэтому —
          строкой-пояснением, тем же серым, что подпись под рядом. */}
      {m.meta?.keyboard?.length ? (
        <div className="chat-meta">
          {t('kbReply', {
            list: m.meta.keyboard.flat().map((b) => b.label).join(' · '),
          })}
        </div>
      ) : m.meta?.kbEvent ? (
        <div className="chat-meta">{t(m.meta.kbEvent === 'removed' ? 'kbRemoved' : 'kbForce')}</div>
      ) : null}
      {/* ПОД ПУЗЫРЁМ — ТОЛЬКО «Бот · Название» / «Ответили вы», «правлено», статус и
          отказ (§3.2). Время отсюда ушло внутрь пузыря; пусто — строки нет вовсе. */}
      {m.direction === 'out' || m.meta?.editedAt || m.meta?.viaBot ? (
        <div className="chat-meta">
          {/* Кто ответил: сценарий или человек из кабинета — видно сразу (заказ
              владельца). У ответа бота подпись ведёт в сам сценарий (М11.4): «а что он
              тут написал?» решается одним касанием, без поисков в списке. */}
          {/* ОТПРАВЛЕНО ЦЕПОЧКОЙ (К15 П9 п.5, владелец 06.09: «у каждого такого
              сообщения в чате подпись, что оно отправлено автоматически таким-то
              ботом»): подпись «Цепочка «Имя»» тем же видом и с тем же переходом, что
              «Бот · <сценарий>» — второго вида подписи под пузырём не заводим. */}
          {m.direction === 'out' &&
          m.sourceKind === 'chain' &&
          scenarioName(m.sourceDraftId ?? m.draftId) ? (
            <Link className="msg-by-scn" href={`/app/bots/${m.sourceDraftId ?? m.draftId}`}>
              {t('byChain', { name: scenarioName(m.sourceDraftId ?? m.draftId) })}
            </Link>
          ) : m.direction === 'out' && m.author === 'scenario' && scenarioName(m.draftId) ? (
            <Link className="msg-by-scn" href={`/app/bots/${m.draftId}`}>
              {t('byBot', { name: scenarioName(m.draftId) })}
            </Link>
          ) : m.direction === 'out' ? (
            // Подпись free (PRICING-PARTNER Э2) уходит от имени бота, но пишем её МЫ —
            // помечаем отдельно, иначе клиент прочтёт её как своё сообщение.
            <span>
              {/* Под служебной подписью — «ответили вы», как в iOS (решение владельца 02.10.2026,
                  В-Ч1; ТЗ WEB-PARITY-3 Ч25): слов «подпись Scriptera» клиент не видит. */}
              {t(m.author === 'scenario' ? 'byScenario' : 'byOwner')}
            </span>
          ) : null}
          {/* ЧЕРЕЗ БОТА (ТЗ CHAT-RENDER-TZ К68, `via_bot`): сообщение набрал не сам
              человек, а инлайн-бот — так помечает его и сама площадка. */}
          {m.meta?.viaBot ? <span>{t('viaBot', { name: m.meta.viaBot })}</span> : null}
          {/* ПРАВКА (С6): человек поправил сказанное — в ленте стоит новый текст, и
              отметка объясняет, почему он разошёлся с тем, что клиент читал раньше. */}
          {m.meta?.editedAt ? <span>{t('editedMark')}</span> : null}
          {/* Судьба нашего сообщения: «отправляется» · «Не отправлено · причина» ·
              «Просмотрено» (С7). Какое слово и кому — общий ответ msg-feed. */}
          {stText ? (
            <span className={m.status === 'failed' ? 'msg-failed' : undefined}>{stText}</span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function DialogPanel({
  dialog: row,
  onChanged,
  onRead,
  onReadDone,
  framed = false,
  account,
  accountLive,
  back,
  nativeScreen = false,
  onClose,
  atMessage = '',
}: {
  /** Строка списка — из неё шапка ленты живёт ещё до ответа сервера. */
  dialog: DialogListRow;
  /** Список диалогов надо перечитать (счётчик непрочитанных, последняя реплика). */
  onChanged: () => void;
  /**
   * ПЕРЕПИСКУ ЧИТАЮТ ПРЯМО СЕЙЧАС (П14 обхода владельца 11.09.2026, круг 2). Зовём ДО
   * ответа сервера, вместе с PATCH: раздел тут же опускает цифры — ряд списка, наклейку
   * активной папки, значок вкладки. `was` — сколько сообщений было не прочитано; правду о
   * нём знает только лента (ряд списка мог приехать снимком минуту назад).
   */
  onRead?: (id: string, was: number) => void;
  /** Сервер прочтение подтвердил: разделу пора перечитать список, папки и соседние
   *  страницы — дальше на экране цифры сервера, а не наша оптимистика (П14). */
  onReadDone?: () => void;
  /** Правое поле второй колонки (широкий экран, владелец 2026-08-01): переписка живёт
   *  в той же раме, что чат ассистента — лента скроллится сама между шапкой и полем,
   *  поле прижато к низу экрана, есть кнопка «к последнему». В шторке (узкий экран и
   *  приложение) рамы нет: там скроллится сам слой, поле липнет снизу как раньше. */
  framed?: boolean;
  /** Имя аккаунта клиента (для карточки собеседника). */
  account?: string;
  /** Жив ли аккаунт/бот этой переписки: false — отключён, писать нельзя ничем.
   *  undefined — ещё не знаем (список аккаунтов не пришёл), тогда молчим. */
  accountLive?: boolean;
  /** «Назад» из шапки рамы (владелец 05.08: слева круглая кнопка, как на iPhone). */
  back?: () => void;
  /** РОДНОЙ ЭКРАН ПЕРЕПИСКИ в приложении (трек Н): ленту рисует и крутит натив, а этот
   *  компонент остаётся мозгом — данные, правила каналов, отправка, словарь. Своей
   *  разметки в этом режиме он не показывает (второй ленты быть не должно). */
  nativeScreen?: boolean;
  /** Родной экран ушёл назад — список закрывает переписку. */
  onClose?: () => void;
  /** ОТКРЫТЬ НА ЭТОЙ РЕПЛИКЕ (О22.5): переписку открыли из поиска — лента приезжает
   *  страницей ВОКРУГ найденного сообщения, встаёт на нём и подсвечивает его. Пусто —
   *  обычное открытие, на последнем сообщении. */
  atMessage?: string;
}) {
  const t = useTranslations('cabinet.messages');
  const tRoot = useTranslations('cabinet');
  const locale = useLocale();
  const router = useRouter();
  const infoPanel = useSlidePanel();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [older, setOlder] = useState<FeedMessage[]>([]);
  // ОТПРАВЛЯЕТСЯ ПРЯМО СЕЙЧАС: свой пузырь встаёт в ленту, не дожидаясь ответа сервера
  // (в мессенджерах сообщение появляется в тот же миг, что нажата отправка). Живёт до
  // следующей страницы ленты — там оно уже есть по-настоящему, со своей судьбой.
  const [pending, setPending] = useState<FeedMessage[]>([]);
  const [headMore, setHeadMore] = useState<boolean | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  // Рама чата ассистента (framed): лента — свой скролл-контейнер, поле прижато к низу.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showJump, setShowJump] = useState(false);
  const onFeedScroll = useCallback(() => {
    const el = scrollRef.current;
    if (el) setShowJump(el.scrollHeight - el.scrollTop - el.clientHeight > 120);
  }, []);
  const jumpToBottom = useCallback(() => {
    const el = scrollRef.current;
    el?.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, []);

  // Открытие из поиска — свой ключ кэша: страница ВОКРУГ реплики и обычный конец
  // переписки не должны подменять друг друга в кэше (владелец открывает и то, и то).
  const { data, loading, error, reload } = usePersistentData<FeedResp>(
    dialogFeedKey(row.id, atMessage),
    dialogFeedUrl(row.id, atMessage),
    { accept: acceptFeed },
  );

  // ЖИВАЯ ПЕРЕПИСКА (владелец 2026-08-04, «чётко как в телеге»). Основной путь — канал-пуш
  // (lib/messages-live.ts): сервер толкает повод «в диалоге X новое», лента перезапрашивает
  // себя штатным reload. Повод без id диалога (значок на пузыре) освежает
  // открытую ленту тоже — что именно поменялось, там неизвестно.
  const live = useMessagesLive(
    useCallback(
      (dialogId: string | null) => {
        if (!dialogId || dialogId === row.id) reload();
      },
      [row.id, reload],
    ),
  );

  // Запасной опрос (ТЗ SPEED §8.1): с живым каналом — НЕТ, поводы приходят каналом; без
  // канала — 15 с и только пока переписка на экране (раздел выбран, документ виден).
  const feedVisible = usePaneVisible();
  useEffect(() => {
    if (live || !feedVisible) return;
    const id = setInterval(() => reload(), 15_000);
    return () => clearInterval(id);
  }, [reload, live, feedVisible]);

  const dialog = data?.dialog;
  const channel = dialog?.channel ?? row.channel;
  const chan = rulesChannel(channel);
  // Пузырь-обещание исчезает РОВНО тогда, когда в странице появилось это же сообщение —
  // иначе на один кадр в ленте стоят оба (обещание и настоящее), и это видно двойником.
  const pendingShown = pending.filter(
    (p) => !(data?.messages ?? []).some((m) => m.direction === 'out' && m.text === p.text),
  );
  // Лёгкий прогрев (20 последних, PREFETCH-CAREFUL §2 п.5): страница «раньше», догруженная до прихода полной,
  // перекрывается с ней — повторы из `older` выбрасываем по id.
  const pageIds = new Set((data?.messages ?? []).map((m) => m.id));
  const olderOnly = older.some((m) => pageIds.has(m.id)) ? older.filter((m) => !pageIds.has(m.id)) : older;
  const messages = [...olderOnly, ...(data?.messages ?? []), ...pendingShown];
  // Пачка файлов, присланная одним альбомом, рисуется одним рядом (С6).
  const rows = feedRows(messages);
  // СКЛЕЙКА ГРУППЫ (паспорт §3.2, docs/CHAT-LOOK-PLAN.md): подряд идущие реплики одной
  // стороны без разделителя и с разрывом до 5 минут стоят одним блоком — углы стыка 6.
  // Считает общий msg-feed: у родной ленты приложения ответ обязан быть тот же.
  const groups = feedGroups(rows, dayKey);
  // ВСЁ МЕДИА ПЕРЕПИСКИ ПО ПОРЯДКУ (владелец 18.08): по нему просмотр листает стрелками и
  // свайпом. Состав списка решает общий msg-feed (feedMedia) — он же под юнитами.
  // Кадры историй из шапки-ответа и у реакции тоже здесь (§3.4): их видно в ленте, значит
  // они открываются у нас.
  const media = feedMedia(messages, { story: t('kindStory'), storyReply: t('replyToStory') });
  /**
   * ПРОСМОТР МЕДИА — НАШИМ СЛОЕМ (заказ владельца 18.08.2026, Ф17.3): на десктопе это
   * наша модалка, выезжающая справа, на мобильном сайте и в приложении — ВЕРТИКАЛЬНАЯ
   * ШТОРКА поверх живой переписки: лист снизу, экран-родитель СТОИТ НА МЕСТЕ, колоды и
   * шаров за ним нет (словарь §11.0, заказ владельца 04.09.2026 — §12.1 п.4 плана
   * CHAT-LOOK; прежнее слово «стопка» этим отменено). Всё это даёт один обычный ход
   * кабинета (useSlidePanel + `sheet: true`, БЕЗ `deck`; канон §11 п.8-10, п.18 «колода
   * бывает только у листа над листом»): своего оверлея у
   * просмотра больше нет — прежний выглядел на десктопе не нашим, а в приложении его
   * перекрывал родной экран, и просмотр не открывался вовсе.
   * onBack у слоя НЕ задаём (§11 п.8): переписка жива под ним, «назад» — это pop.
   * Нажатое медиа ищем в списке по адресу: нашли — листается вся переписка, не нашли
   * (кадр истории из шапки ответа — отдельной репликой его в ленте нет) — открываем одно.
   */
  const viewPanel = useSlidePanel();
  // Обычная функция, не useCallback: тело слоя — СНИМОК на момент открытия, и состав
  // медиа берётся из этого же рендера (запоминать его в ref незачем и нечем — лента
  // меняется каждым тиком закачки).
  const openMedia = (item: FeedMedia) => openFeedMedia(viewPanel, t, media, item);
  // Отметку «просмотрено» (С7) несёт РОВНО ОДНА реплика — последняя наша прочитанная;
  // кто именно, решает общий msg-feed, а не лента: у приложения ответ обязан быть тот же.
  const markId = readMarkId(messages);
  const hasOlder = headMore ?? data?.more ?? false;
  // Названия сценариев для подписей: приходят вместе со страницей ленты, страницы «раньше»
  // добавляют свои — иначе подпись у старых ответов пропадала бы при подгрузке.
  const [olderNames, setOlderNames] = useState<Record<string, string>>({});
  const scenarioName = useCallback(
    (id: unknown): string =>
      typeof id === 'string' && id ? (data?.scenarios?.[id] ?? olderNames[id] ?? '') : '',
    [data, olderNames],
  );

  // Пузырь-обещание живёт, ПОКА сервер не показал это же сообщение по-настоящему. Раньше
  // он снимался на любую свежую страницу — и если она пришла раньше записи, пузырь на миг
  // пропадал и появлялся снова (дёрганье на отправке, видео владельца 2026-08-08).
  useEffect(() => {
    if (!pending.length) return;
    const mine = new Set(
      (data?.messages ?? []).filter((m) => m.direction === 'out').map((m) => m.text),
    );
    if (pending.every((p) => mine.has(p.text))) setPending([]);
  }, [data, pending]);

  // Открытая переписка прочитана. Не «один раз на диалог»: с живым опросом новое
  // входящее приходит, пока человек СМОТРИТ на него — счётчик в списке рос бы у
  // прочитанного (row.unread из панели не обновляется — тело слоя создано снимком,
  // поэтому правду о непрочитанном берёт лента: data.dialog.unread).
  //
  // ПЕРВОЕ ЧИСЛО — ОТ РЯДА, А НЕ ОТ ЛЕНТЫ (П24 обхода владельца 11.09.2026, круг 3).
  // Лента приезжает из кэша кабинета, и у переписки, читанной прошлым заходом, в кэше
  // стоит прежний НОЛЬ: прочтение ждало бы свежего ответа сервера — сотни миллисекунд,
  // уже после того, как натив снял список под экраном переписки (в `chatOpen`), и
  // наклейка пропадала бы при человеке. Снимок ряда знает правду первым же кадром, им и
  // считаем на монтировании; дальше он тратится (снимок не растёт).
  const [readSeed, setReadSeed] = useState(row.unread);
  const feedUnread = data?.dialog?.unread;
  const unread = unreadToRead(feedUnread, readSeed);
  const readBusyRef = useRef(false);
  /** Что прочитано прошлым разом (messages-read.ts): одно и то же число два раза не
   *  вычитаем — ответ ленты, ушедший ДО нашего PATCH, несёт старое. Лента показала
   *  ноль — память чиста, следующая порция читается как первая. */
  const lastReadRef = useRef(0);
  useEffect(() => {
    lastReadRef.current = lastReadAfterFeed(lastReadRef.current, feedUnread);
  }, [feedUnread]);
  // Руки раздела — РЕФОМ: у оболочки они пересобираются каждым рендером, а этот эффект
  // обязан сработать РОВНО ОДИН раз на непрочитанное (иначе второй заход послал бы второй
  // PATCH, пока первый ещё в пути).
  const readHandsRef = useRef({ now: onRead, done: onReadDone });
  useEffect(() => {
    readHandsRef.current = { now: onRead, done: onReadDone };
  });
  useEffect(() => {
    if (readBusyRef.current || !readAgain(lastReadRef.current, unread)) return;
    readBusyRef.current = true;
    lastReadRef.current = unread;
    setReadSeed(0);
    // ЦИФРЫ ОПАДАЮТ СРАЗУ (П14 обхода владельца 11.09.2026, круг 2): человек открыл
    // переписку — она прочитана, и список не имеет права показывать её непрочитанной,
    // пока идёт PATCH и следующий заход за списком. Журналом (DIAGNOSTICS-CANON) кладём
    // и адрес переписки, и прежнее число: по жалобе «цифра не та» видно, с чего считали.
    // `feed` в записи — что говорила лента: по нему видно, ряд или лента дал число.
    journalLog('msgs.read', { id: row.id, was: unread, feed: feedUnread ?? -1 });
    readHandsRef.current.now?.(row.id, unread);
    void fetch(`/api/cabinet/messages/${row.id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ read: true }),
    })
      .then(() => {
        onChanged();
        readHandsRef.current.done?.();
      })
      .catch(() => {
        // Не прочиталось — счётчик просто останется; память о числе снимаем, чтобы
        // следующая попытка была законной.
        lastReadRef.current = 0;
      })
      .finally(() => {
        readBusyRef.current = false;
      });
    // feedUnread в зависимостях не нужен: он входит в `unread`, а в запись журнала
    // берётся тем же кадром, в котором эффект сработал.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.id, unread, onChanged]);

  // Лента открывается на последнем сообщении, как в мессенджере.
  // Прилипание к низу (владелец 2026-08-01, вторая проба: «так и не доскроливается»).
  // Причина была во времени: место под РОДНОЕ поле приложения приезжает отдельным
  // событием (--native-composer-pad), картинки и видео досчитывают высоту тоже позже —
  // а мы держали низ всего полторы секунды. Теперь держим, ПОКА человек сам не отмотал
  // вверх: любое изменение высоты ленты (её самой, её рядов) или отступа под полем
  // возвращает нас в конец.
  // Открыли на найденной реплике (О22.5) — к низу НЕ липнем: человек пришёл читать
  // именно это место переписки.
  // Найденная реплика в ленте: по ней лента и встаёт (О22.5).
  const hitRef = useRef<HTMLElement | null>(null);
  const hitDoneRef = useRef(false);
  // РОДНАЯ ЛЕНТА ВСТАЁТ НА НАЙДЕННУЮ РЕПЛИКУ (круг 11, §17.0 п.8): вместе с первой
  // страницей, где реплика есть, нативу уезжает необязательное поле `focus` — id ряда.
  // Ровно один раз: следующие пересчёты ленты (опрос, набор) взгляд не дёргают.
  const focusSentRef = useRef(false);
  useEffect(() => {
    if (!atMessage || hitDoneRef.current) return;
    const el = hitRef.current;
    if (!el) return;
    hitDoneRef.current = true;
    // По центру экрана: у найденного сообщения важен контекст — что было до и после.
    el.scrollIntoView({ block: 'center' });
  }, [atMessage, data]);

  const stickRef = useRef(!atMessage);
  // В САМЫЙ низ, а не «показать последний ряд»: scrollIntoView ставил последнее
  // сообщение вровень с нижней кромкой — и оно пряталось под полем. Крутим скроллер до
  // упора: запас под поле уходит ПОД него, последнее сообщение стоит над.
  // Кто именно крутится — решает feedScroller (рама / тело шторки / документ), гадать
  // по ширине экрана нельзя: у веб-шторки свой скроллер, и «крутим окно» там не делало
  // ничего — переписка открывалась в самом НАЧАЛЕ (жалоба владельца 2026-08-02).
  const toEnd = useCallback(() => {
    // Родной экран: лентой владеет натив — крутить веб-документ нельзя (за карточкой
    // стоит список переписок, и мы бы увезли ЕГО).
    if (nativeScreen || !stickRef.current) return;
    const box = feedScroller(scrollRef.current, framed);
    if (box) box.scrollTop = box.scrollHeight;
    else window.scrollTo({ top: document.documentElement.scrollHeight });
  }, [framed, nativeScreen]);

  // Снимок прокрутки того, кто ленту крутит (рама / тело шторки / документ) — по нему
  // считается якорь подгрузки «раньше» (П7).
  const readScroll = useCallback((): { box: HTMLElement | null; top: number; height: number } => {
    const box = feedScroller(scrollRef.current, framed);
    return box
      ? { box, top: box.scrollTop, height: box.scrollHeight }
      : { box: null, top: window.scrollY, height: document.documentElement.scrollHeight };
  }, [framed]);

  // Палец важнее любого нашего доезда: тащит ленту — не мешаем ей ни кадра.
  const dragRef = useRef(false);
  useEffect(() => {
    const on = () => {
      dragRef.current = true;
    };
    const off = () => {
      dragRef.current = false;
    };
    document.addEventListener('touchstart', on, { passive: true });
    document.addEventListener('touchend', off, { passive: true });
    document.addEventListener('touchcancel', off, { passive: true });
    return () => {
      document.removeEventListener('touchstart', on);
      document.removeEventListener('touchend', off);
      document.removeEventListener('touchcancel', off);
    };
  }, []);

  // useLayoutEffect, а не useEffect (П6, владелец 2026-08-06 «сразу в конце»): прокрутка
  // встаёт ДО первой отрисовки — переписка открывается концом ленты, а не показывает на
  // кадр её начало. Высоту под медиа держит CSS (`.msg-media` — коробка с aspect-ratio),
  // поэтому догрузившаяся картинка ленту уже не двигает; остальное досчитывают наблюдатели
  // ниже.
  useLayoutEffect(() => {
    if (!data) return;
    toEnd();
    setShowJump(false);
    // Лента открывается РОВНО в конце и не дёргается (владелец 2026-08-01, разбор видео):
    // картинки, видео и голосовые досчитывают свою высоту уже после первой отрисовки, а
    // слой в это время ещё едет. Держим низ, ПОКА человек сам не отмотал вверх (stickRef):
    // любое изменение высоты ленты или её рядов возвращает нас в конец.
    const feed = scrollRef.current;
    const ro = new ResizeObserver(() => toEnd());
    // В раме лента — свой скроллер: её собственная коробка не меняется, растут только
    // ряды. Поэтому наблюдаем и за рядами, и за появлением НОВЫХ (пришло сообщение,
    // подгрузились старые) — снимок детей на момент подписки протухает через 3 секунды.
    const watch = (): void => {
      if (!feed) return;
      ro.observe(feed); // сама лента: меняется её отступ под родным полем
      for (const el of feed.children) ro.observe(el); // ряды: медиа досчитывает высоту
    };
    watch();
    const mo = new MutationObserver(watch);
    if (feed) mo.observe(feed, { childList: true });
    feed?.addEventListener('load', toEnd, true); // load всплывает только в фазе захвата
    const raf = requestAnimationFrame(toEnd);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      mo.disconnect();
      feed?.removeEventListener('load', toEnd, true);
    };
  }, [data]);
  // Отмотал вверх — перестаём тянуть вниз; вернулся к низу — снова держим.
  useEffect(() => {
    // Скроллер ищем В МОМЕНТ события, а не при подписке (владелец 2026-08-09: «в вебе
    // не скроллится к концу диалога при открытии»). В момент открытия слоя его ещё нет
    // — лента короче экрана, — и пойманный тогда null заставлял судить по ДОКУМЕНТУ:
    // первая же прокрутка страницы под слоем (её делает сам слой) снимала прилипание,
    // и доезд в конец больше не работал — переписка стояла в начале.
    const check = () => {
      const box = feedScroller(scrollRef.current, framed);
      stickRef.current = box
        ? box.scrollHeight - box.scrollTop - box.clientHeight < 140
        : document.documentElement.scrollHeight - window.scrollY - window.innerHeight < 140;
    };
    // Событие scroll НЕ всплывает — слушаем в фазе захвата: один слушатель ловит и
    // документ, и тело шторки, и раму разворота.
    document.addEventListener('scroll', check, true);
    return () => document.removeEventListener('scroll', check, true);
  }, [data, framed]);

  /**
   * ВЫСОТА НИЗА ПЕРЕПИСКИ — ЖИВЫМ ЗАМЕРОМ (правило 6 владельца 09.09.2026, круг 11 плана
   * CHAT-LOOK): в раме (вторая колонка десктопа, iPad-веб) поле ответа вынуто из потока и
   * стоит НАД лентой, поэтому место под него лента держит своим нижним полем. Раньше там
   * стояло фиксированное 116 — догадка: поле в две строки или с подсказкой «ответ на
   * комментарий» вырастало, и последняя реплика уходила под него. Меряем сам элемент низа
   * (поле ответа ИЛИ плашку закрытого окна) и кладём число переменной — воздух над ним
   * CSS добавляет сам, одним `--msg-air`.
   *
   * Ref-функцией, а не эффектом: элемент низа ПОДМЕНЯЕТСЯ (поле ↔ плашка), и наблюдателя
   * надо переселять вместе с ним. Тот же приём, что у высоты шапки (`--msg-head-h`).
   */
  const botObsRef = useRef<ResizeObserver | null>(null);
  const bottomRef = useCallback((el: HTMLDivElement | null) => {
    botObsRef.current?.disconnect();
    botObsRef.current = null;
    if (!el) return;
    const root = el.closest('.msg-dlg');
    if (!(root instanceof HTMLElement)) return;
    const sync = () => {
      const h = el.offsetHeight;
      // Поля нет вовсе (в приложении его рисует натив, веб-поле спрятано) — мерить нечего:
      // переменную не трогаем, и лента живёт запасным числом CSS.
      if (h > 0) root.style.setProperty('--msg-bot-h', `${h}px`);
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    botObsRef.current = ro;
  }, []);
  useEffect(() => () => botObsRef.current?.disconnect(), []);

  /**
   * НИЗ ЛЕНТЫ — ОДНА цифра на все среды: `--msg-inset` на <html> = сколько низа переписки
   * закрыто полем ответа и клавиатурой. По ней CSS даёт ленте место доскроллить и держит
   * веб-поле над клавиатурой.
   *
   * INVARIANT(scriptera): низ считаем ОДНИМ числом и никогда не складываем «отступ под
   * родным полем» с «высотой клавиатуры». Причина (владелец 2026-08-06, скрины
   * IMG_1391–1393): раньше их было два — натив шлёт перекрытие своим полем
   * (`--native-composer-pad`, при открытой клавиатуре оно включает и её), веб отдельно
   * мерил клавиатуру, — и они расходились: при открытой клавиатуре низа не хватало
   * (последнее сообщение под полем), после её закрытия оставался протухший запас
   * (пустая дыра во весь экран). Отсюда правило: высота клавиатуры берётся из замера
   * (натив `--native-kb` или ужатие visualViewport), а высота САМОГО поля — только тогда,
   * когда её можно отделить от клавиатуры, и запоминается; протухшее перекрытие в счёт
   * не идёт. Канон: docs/concepts/IOS-KEYBOARD-CANON.md §1, docs/MESSAGES-PLAN.md.
   *
   * INVARIANT(scriptera): смена низа ВСЕГДА едет анимацией с одновременным доездом ленты
   * (кадр в кадр), а не мгновенным скачком отступа. Причина (владелец 2026-08-06, «всё
   * дёргается»): клавиатура iOS едет ~250 мс, и мгновенно выросший отступ уводил ленту
   * вверх раньше клавиатуры — это и был видимый рывок. Пока лента едет вместе с ней,
   * движения не видно вовсе.
   */
  const insetToRef = useRef(-1);
  const insetNowRef = useRef(0);
  // Два перекрытия родным полем: в покое (с полоской home) и над клавиатурой (без неё).
  // Стартуют из памяти сессии — иначе первая клавиатура в КАЖДОЙ открытой переписке
  // считалась бы заново и давала перелёт (владелец 2026-08-07).
  const restRef = useRef(composerMemo.rest);
  const overRef = useRef(composerMemo.over);
  const animRef = useRef(0);
  /** Поставить низ БЕЗ анимации (родное поле вернулось после карточки собеседника). */
  const snapInsetRef = useRef<(to: number) => void>(() => {});
  useEffect(() => {
    // Родной экран сам берёт низ от клавиатуры (натив, Н4.2) — веб-арифметика тут лишняя
    // и вредна: два механизма на одну ленту — это ровно та болезнь, от которой уходим.
    if (nativeScreen) return;
    const root = document.documentElement;
    const cssVar = (name: string): number =>
      parseFloat(getComputedStyle(root).getPropertyValue(name)) || 0;
    const measure = (): { inset: number; kb: number } => {
      const vv = window.visualViewport;
      const shrunk = vv ? Math.max(0, window.innerHeight - (vv.offsetTop + vv.height)) : 0;
      const kb = Math.max(cssVar('--native-kb'), shrunk);
      const got = feedInset({
        kb,
        pad: cssVar('--native-composer-pad'),
        native: root.classList.contains('chat-native-composer'),
        rest: restRef.current,
        over: overRef.current,
      });
      restRef.current = got.rest;
      overRef.current = got.over;
      composerMemo.rest = got.rest;
      composerMemo.over = got.over;
      return { inset: got.inset, kb };
    };
    const animate = (to: number): void => {
      cancelAnimationFrame(animRef.current);
      const from = insetToRef.current < 0 ? to : insetNowRef.current;
      insetToRef.current = to;
      const t0 = performance.now();
      const dur = from === to ? 0 : 260; // столько же едет клавиатура iOS
      const step = (now: number): void => {
        const k = dur <= 0 ? 1 : Math.min(1, (now - t0) / dur);
        const eased = 1 - (1 - k) ** 3; // мягкий выход, как у клавиатуры
        insetNowRef.current = Math.round(from + (to - from) * eased);
        root.style.setProperty('--msg-inset', `${insetNowRef.current}px`);
        // Кадр в кадр: отступ вырос — лента тут же доехала, движения не видно.
        if (!dragRef.current) toEnd();
        if (k < 1) animRef.current = requestAnimationFrame(step);
      };
      animRef.current = requestAnimationFrame(step);
    };
    // Мгновенно, без 260 мс: поле возвращается ВМЕСТЕ с низом, а не догоняет его.
    snapInsetRef.current = (to: number): void => {
      cancelAnimationFrame(animRef.current);
      insetToRef.current = to;
      insetNowRef.current = to;
      root.style.setProperty('--msg-inset', `${to}px`);
      // Поле возвращается после карточки собеседника — клавиатуры в этот момент нет.
      root.classList.remove('msg-kb-on');
      if (!dragRef.current) toEnd();
    };
    const apply = (): void => {
      const { inset: next, kb } = measure();
      // Признак клавиатуры — сам её замер, а не «низ больше высоты поля»: сравнение
      // двух чисел путалось ровно там, где путались перекрытия (владелец 2026-08-07).
      // Класс пишем ТОЛЬКО при реальной смене: запись атрибута будит свой же
      // MutationObserver, и «переустановка того же» закрутила бы вечный круг.
      const kbOn = kb > 0;
      if (root.classList.contains('msg-kb-on') !== kbOn) root.classList.toggle('msg-kb-on', kbOn);
      // Без сравнения запись свойства разбудила бы собственный MutationObserver.
      if (next === insetToRef.current) return;
      animate(next);
    };
    apply();
    const vv = window.visualViewport;
    vv?.addEventListener('resize', apply);
    vv?.addEventListener('scroll', apply);
    // Приложение шлёт высоту клавиатуры переменной на <html> (native-keyboard.ts), натив
    // родного поля — своим перекрытием (--native-composer-pad): и то и другое сменой style.
    const mo = new MutationObserver(apply);
    mo.observe(root, { attributes: true, attributeFilter: ['class', 'style'] });
    // Фокус в поле переписки: клавиатура ещё не поехала, а низ держать надо уже сейчас
    // (канон IOS-KEYBOARD §5.3: детекция от фокуса, а не от готового ужатия).
    const onFocus = (e: FocusEvent): void => {
      const el = e.target;
      if (el instanceof HTMLElement && el.closest('.msg-dlg')) {
        stickRef.current = true;
        toEnd();
      }
    };
    document.addEventListener('focusin', onFocus);
    return () => {
      cancelAnimationFrame(animRef.current);
      vv?.removeEventListener('resize', apply);
      vv?.removeEventListener('scroll', apply);
      document.removeEventListener('focusin', onFocus);
      mo.disconnect();
      root.style.removeProperty('--msg-inset');
      root.classList.remove('msg-kb-on');
    };
  }, [toEnd, nativeScreen]);

  // «Отвечаю сам» (М9): пока отметка стоит, сценарий в этот диалог не пишет — замок
  // проверяют оба приёмника. Состояние ведём местно, чтобы переключатель отзывался
  // сразу; правда — за сервером, поэтому после ответа перечитываем переписку.
  const [mine, setMine] = useState<boolean | null>(null);
  const takenOver = mine ?? dialog?.takenOver ?? row.takenOver ?? false;
  const toggleMine = async () => {
    const next = !takenOver;
    setMine(next);
    // Карточка собеседника («i») лежит ОТДЕЛЬНЫМ слоем и её содержимое снято в момент
    // открытия — сама она о переключении не узнает и показывала прежнюю надпись
    // (владелец 2026-08-09, IMG_1526). Поэтому состояние замка объявляем событием: кто
    // его показывает, тот и слушает. Второе событие — если сервер отказал.
    tellMine(row.id, next);
    try {
      const r = await fetch(`/api/cabinet/messages/${row.id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ takeOver: next }),
      });
      if (!r.ok) throw new Error('takeover');
      reload();
      onChanged();
    } catch {
      setMine(!next); // не встало — возвращаем переключатель, врать нельзя
      tellMine(row.id, !next);
    }
  };

  // ПОДГРУЗКА «РАНЬШЕ» БЕЗ ПРЫЖКА (П7, владелец 2026-08-06). Старые сообщения встают в
  // НАЧАЛО ленты — всё, что человек читал, уехало бы вниз на высоту вставки. Запоминаем
  // прокрутку и высоту ДО вставки и возвращаем взгляд на то же место в том же кадре
  // (useLayoutEffect — до отрисовки, иначе прыжок видно).
  const anchorRef = useRef<{ top: number; height: number } | null>(null);
  /** Страница «раньше» уже в пути (синхронный замок авто-подгрузки, Ф10.12). */
  const olderBusyRef = useRef(false);
  useLayoutEffect(() => {
    const keep = anchorRef.current;
    if (!keep) return;
    anchorRef.current = null;
    const now = readScroll();
    const top = anchorTop(keep.top, keep.height, now.height);
    if (now.box) now.box.scrollTop = top;
    else window.scrollTo({ top });
  }, [older, readScroll]);

  const loadOlder = async () => {
    const first = messages[0];
    // ЗАМОК СИНХРОННЫЙ, А НЕ ПО busy (Ф10.12): события прокрутки идут пачкой в одном
    // тике, а `busy` встаёт только следующим рендером — без ref-замка авто-подгрузка
    // успевала запросить одну и ту же страницу дважды и удваивала сообщения в ленте.
    if (!first || busy || olderBusyRef.current) return;
    olderBusyRef.current = true;
    setBusy(true);
    try {
      const r = await fetch(
        `/api/cabinet/messages/${row.id}?before=${encodeURIComponent(first.at)}`,
        { cache: 'no-store' },
      );
      const j = (await r.json()) as FeedResp;
      if (j?.ok) {
        // Низ ОТПУСКАТЬ НЕЛЬЗЯ (Ф10.12): прилипание ведёт слушатель прокрутки — он и так
        // снимает его, едва человек ушёл вверх. А с авто-подгрузкой страница «раньше»
        // приезжает и когда человек стоит в конце короткой переписки: сброс прилипания
        // там ломал доезд в конец (лента переставала возвращаться к последнему пузырю,
        // пока медиа досчитывает высоту).
        // Родная лента удержит взгляд на месте, если знает, что страница встаёт СВЕРХУ.
        prependRef.current = true;
        const before = readScroll();
        anchorRef.current = { top: before.top, height: before.height };
        setOlder((prev) => [...j.messages, ...prev]);
        // Пустая страница = раньше ничего нет, чем бы ни ответил сервер: иначе
        // авто-подгрузка (Ф10.12) просила бы её снова и снова.
        setHeadMore(j.messages.length > 0 && j.more);
        if (j.scenarios) setOlderNames((prev) => ({ ...prev, ...j.scenarios }));
      }
    } catch {
      /* обрыв сети — попросим ту же страницу на следующей прокрутке */
    } finally {
      olderBusyRef.current = false;
      setBusy(false);
    }
  };

  // Коды сервера — на человеческий; незнакомый (сервер новее кабинета) — общей строкой.
  const errText = useCallback(
    (code: string | undefined) => {
      const known = [
        'window_closed',
        'comment_text_only',
        'attach_not_supported',
        'file_too_big',
        'file_format',
        'no_account',
        'empty',
        'voice_failed',
      ];
      return t(known.includes(code ?? '') ? `err_${code}` : 'err_send');
    },
    [t],
  );

  const send = useCallback(
    async (payload: { kind: string; text?: string; file?: FeedFile }) => {
      setBusy(true);
      setErr('');
      // Только текст: файл и голосовое до отправки ещё заливаются, и пузырь-обещание
      // висел бы дольше самой отправки.
      if (payload.kind === 'text' && payload.text) {
        // Поле пустеет СРАЗУ, как в мессенджерах, а не после ответа сервера: ждать
        // кругосветку ради очистки — это и есть «залипшая» строка ввода.
        setText('');
        setPending([
          {
            id: `pending:${payload.text}`,
            direction: 'out',
            author: 'owner',
            kind: 'text',
            via: 'direct',
            text: payload.text,
            file: null,
            buttons: [],
            replyTo: null,
            status: 'sending',
            error: '',
            draftId: null,
            eventType: null,
            eventData: null,
            at: new Date().toISOString(),
          },
        ]);
      }
      try {
        const r = await fetch(`/api/cabinet/messages/${row.id}`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const j = (await r.json()) as { ok?: boolean; error?: string };
        if (!j?.ok) {
          setErr(errText(j?.error));
          // Не ушло — обещание снимаем и ВОЗВРАЩАЕМ слова в поле: терять написанное нельзя.
          setPending([]);
          if (payload.kind === 'text' && payload.text) setText(payload.text);
        } else setText('');
        // Отправил — лента едет к СВОЕМУ сообщению, даже если был отмотан вверх
        // (как в Телеграме). Держим низ, пока сообщение встаёт в ленту: поле ответа при
        // этом ужимается обратно в одну строку, и без прилипания это видно рывком.
        stickRef.current = true;
        toEnd();
      } catch {
        setErr(t('err_send'));
        setPending([]);
        if (payload.kind === 'text' && payload.text) setText(payload.text);
      } finally {
        setBusy(false);
        reload();
        onChanged();
      }
    },
    [row.id, reload, onChanged, t, errText, toEnd],
  );

  // Голосовое: запись уходит своим роутом — там её перегоняют в формат канала и шлют
  // голосовым, а не файлом. Пока идёт перегон, композер занят (busy).
  const sendVoice = useCallback(
    async (blob: Blob, seconds: number) => {
      setBusy(true);
      setErr('');
      try {
        const form = new FormData();
        form.append('file', blob, 'voice');
        form.append('seconds', String(seconds));
        const r = await fetch(`/api/cabinet/messages/${row.id}/voice`, {
          method: 'POST',
          body: form,
        });
        const j = (await r.json()) as { ok?: boolean; error?: string };
        if (!j?.ok) setErr(errText(j?.error));
        stickRef.current = true; // голосовое ушло — лента едет к нему (как в Телеграме)
        toEnd();
      } catch {
        setErr(t('err_send'));
      } finally {
        setBusy(false);
        reload();
        onChanged();
      }
    },
    [row.id, reload, onChanged, t, errText, toEnd],
  );

  // Скрепка: файл сразу уходит человеку, текст поля становится подписью — как в мессенджере.
  // `caption` приходит только из РОДНОГО превью приложения (там подпись набирают в самом
  // листе); у веб-выбора её нет, и подписью служит то, что стоит в поле ответа.
  const onPickFile = async (file: File | undefined, caption?: string) => {
    if (!file) return;
    const kind = attachKindOf((file.type || '').toLowerCase(), file.name);
    const limit = chatAttachLimit(chan, kind);
    if (!limit) {
      setErr(t('err_attach_not_supported'));
      return;
    }
    if (file.size > limit.maxBytes) {
      setErr(t('err_file_too_big'));
      return;
    }
    setBusy(true);
    setErr('');
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await fetch(`/api/cabinet/scenarios/files?kind=${kind}&channel=${chan}`, {
        method: 'POST',
        body: form,
      });
      const j = (await r.json()) as { ok?: boolean; url?: string; name?: string; error?: string };
      if (!j?.ok || !j.url) {
        setErr(t(j?.error === 'too_large' ? 'err_file_too_big' : j?.error === 'bad_type' ? 'err_file_format' : 'err_send'));
        setBusy(false);
        return;
      }
      setBusy(false);
      await send({
        kind,
        text: (caption ?? text).trim() || undefined,
        file: { url: j.url, name: j.name ?? file.name, mime: file.type, bytes: file.size },
      });
    } catch {
      setErr(t('err_send'));
      setBusy(false);
    }
  };

  // ── Родное поле ввода в приложении (MESSAGES М12) ────────────────────────────────
  // В приложении писать человеку нужно НАСТОЯЩИМ полем над клавиатурой, как в Телеграме:
  // веб-поле дерётся с клавиатурой и прыгает. Поле, микрофон, отправку и скрепку рисует
  // натив (тот же родной композер, что у чата с ассистентом — близнеца не заводим), веб
  // остаётся хозяином смысла: что можно приложить, что уходит голосовым, что подписью.
  // Feature-detect по PluginHeaders (nativeMethod), НЕ по свойству — капкан Proxy Capacitor.
  // Сборка без скрепки родное поле НЕ получает: иначе в переписке не осталось бы ни
  // родной скрепки, ни веб-композера (при родном поле его прячут), и файл стало бы не
  // приложить вовсе.
  const nativeComposer = nativeMethod('configureComposer') && nativeMethod('composerAttachSupported');
  // Родной ЭКРАН переписки (трек Н) считаем здесь, до родного поля: поле обязано про него
  // знать (см. composerOn ниже). Мост тот же, что и ниже по файлу, — второго признака нет.
  const nativeChat = nativeScreen && nativeMethod('chatSupported');
  /** Карточку собеседника рисует НАТИВ (волна 2, Опус-В2Г). Признак — наличие метода
   *  (PluginHeaders), не свойство: капкан Proxy Capacitor. Старая сборка метода не
   *  имеет — карточка живёт прежней веб-шторкой. */
  const nativeInfoCard = nativeChat && nativeMethod('chatInfoOpen');
  /**
   * ПОЛЕ ОТВЕТА ПОЯВЛЯЕТСЯ, КОГДА ЭКРАН ПЕРЕПИСКИ ДОЕХАЛ (Ф12, владелец 18.08).
   * Родное поле — отдельная нативная строка НАД всем экраном, а не часть карточки
   * переписки. Открывая родной экран, натив фотографирует то, что под ним (список
   * «Чаты»), и держит этот снимок всю жизнь экрана; в снимок попадало и поле, если оно
   * успело встать раньше. Закрываешь переписку — карточка уезжает вправо со своим живым
   * полем, а на возвращающемся снимке списка СВЕТИТСЯ его фотография, и гаснет она
   * только когда снимок снимают, то есть ровно когда выезд доехал. Поэтому поле ставим
   * ПОСЛЕ того, как экран открылся и приехал: снимок под ним чистый, а поле уезжает
   * вместе с карточкой, как и было.
   * Ключ — id переписки: открыли другую, поле ждёт её экран заново.
   */
  const [chatUpFor, setChatUpFor] = useState('');
  const composerOn = nativeComposer && (!nativeChat || chatUpFor === row.id);
  // Голос — тем же общим хуком, что и веб-поле (инвариант use-voice-input: логика записи
  // одна). Распознавание не зовём: человеку уезжает сам звук (М7).
  const nativeVoice = useVoiceInput({
    onBlob: (blob, seconds) => void sendVoice(blob, seconds),
    onText: () => {},
    onError: () => setErr(t('voiceError')),
    onLevel: (level) => {
      if (nativeComposer) void nativeBridge()?.setComposerLevel?.({ level });
    },
  });
  const textRef = useRef(text);
  textRef.current = text;
  const sendRef = useRef<() => void>(() => {});
  sendRef.current = () => {
    const v = textRef.current.trim();
    if (v) void send({ kind: 'text', text: v });
  };
  const micRef = useRef(nativeVoice.toggle);
  micRef.current = nativeVoice.toggle;
  const micCancelRef = useRef(nativeVoice.cancel);
  micCancelRef.current = nativeVoice.cancel;
  // ── РОДНОЙ ВЫБОР ФАЙЛА (волна 2, Опус-В2Г; лист-превью убран волной 3) ───────────
  // Скрепка родного поля открывает системный выбор, а сюда возвращается выбранный файл
  // (событие chatFile) — заливка и отправка идут ПРЕЖНИМ путём (onPickFile), второго
  // пути отправки в переписку нет. Подтверждения-превью с отдельным полем подписи БОЛЬШЕ
  // НЕТ (владелец 01.09: «как в вебе»): файл уходит сразу, подписью служит то, что стоит
  // в поле ответа, а «формат не поддерживается» и «файл слишком большой» говорит веб —
  // там же, где говорит в вебе (строка ошибки у поля).
  // Что можно приложить и какой предел у канала, решает ТОЛЬКО веб (свод правил
  // каналов): натив ни про каналы, ни про форматы не знает и ничего не додумывает.
  const nativeAttach = composerOn && nativeMethod('chatAttachPick');
  const attachAsk = (): NativeChatAttachAsk => {
    const kinds = CHAT_ATTACH_KINDS.filter((k) => !!chatAttachLimit(chan, k));
    return {
      theme: document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light',
      photos: kinds.includes('image') || kinds.includes('video'),
      videos: kinds.includes('video'),
      files: kinds.includes('file'),
      // Форматы документа — тот же список, что стоит у веб-поля выбора; пусто у
      // Телеграма (он принимает документом что угодно).
      accept: [...(chatAttachLimit(chan, 'file')?.accept ?? [])],
      bridgeBytes: BRIDGE_FILE_BYTES,
      dict: {
        title: t('attach'),
        cancel: tRoot('cancel'),
        photo: t('kindImage'),
        file: t('kindFile'),
      },
    };
  };
  // ЛИСТ «ПРИЛОЖИТЬ ФАЙЛ» НА ТЕЛЕФОНЕ В БРАУЗЕРЕ (ТЗ WEB-PARITY-3 Ч24, кадр iOS C07-chat-attach):
  // «Фото» → выбор картинки/видео, «Файл» → любой файл; на широком экране — сразу системный выбор.
  const [attachPick, setAttachPick] = useState(false);
  const attachRef = useRef<() => void>(() => {});
  attachRef.current = () => {
    if (nativeAttach) {
      void nativeBridge()?.chatAttachPick?.(attachAsk());
      return;
    }
    const phone =
      typeof window !== 'undefined' &&
      !document.documentElement.classList.contains('app') &&
      window.matchMedia('(max-width: 820px)').matches;
    if (phone && !!chatAttachLimit(chan, 'file') && (!!chatAttachLimit(chan, 'image') || !!chatAttachLimit(chan, 'video'))) {
      setAttachPick(true);
      return;
    }
    fileRef.current?.click();
  };
  const composerState: 'idle' | 'busy' | 'recording' | 'transcribing' = nativeVoice.recording
    ? 'recording'
    : busy
      ? 'busy'
      : 'idle';
  const [themeTick, setThemeTick] = useState(0);

  useEffect(() => {
    if (!composerOn) return;
    const br = nativeBridge();
    if (!br?.configureComposer) return;
    document.documentElement.classList.add('chat-native-composer');
    let dead = false;
    const subs: Array<{ remove: () => void | Promise<void> }> = [];
    void (async () => {
      const add = async (
        ev: 'composerInput' | 'composerSubmit' | 'composerMic' | 'composerCancel' | 'composerResize' | 'composerAttach',
        cb: (d: { text?: string; pad?: number }) => void,
      ): Promise<void> => {
        const sub = await br.addListener!(ev, cb);
        if (dead) void sub.remove();
        else subs.push(sub);
      };
      await add('composerInput', (d) => setText(d?.text ?? ''));
      await add('composerSubmit', () => sendRef.current());
      await add('composerMic', () => micRef.current());
      await add('composerCancel', () => micCancelRef.current());
      await add('composerAttach', () => attachRef.current());
      await add('composerResize', (d) => {
        document.documentElement.style.setProperty(
          '--native-composer-pad',
          `${Math.max(0, Number(d?.pad ?? 0))}px`,
        );
      });
    })();
    const obs = new MutationObserver(() => setThemeTick((x) => x + 1));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'data-tint'] });
    return () => {
      dead = true;
      obs.disconnect();
      for (const sub of subs) void sub.remove();
      void br.clearComposer?.();
      document.documentElement.classList.remove('chat-native-composer');
      document.documentElement.style.removeProperty('--native-composer-pad');
    };
  }, [composerOn]);

  // Карточка «О собеседнике» открыта (владелец 05.08, IMG_1293): РОДНОЕ поле и
  // клавиатура — нативные вьюхи, они рисуются ПОВЕРХ листа; веб их не прячет ничем,
  // кроме снятия самого поля (clearComposer роняет и клавиатуру). Закрыли лист —
  // поле возвращается (эффект ниже).
  const [infoOpen, setInfoOpen] = useState(false);
  /** Карточку показывает РОДНОЙ лист (волна 2, Опус-В2Г) — тогда её уход приезжает
   *  событием chatInfoGone, а не сменой верхнего веб-слоя. */
  const [infoNative, setInfoNative] = useState(false);
  useEffect(() => {
    if (!infoOpen || infoNative) return;
    const openedAt = Date.now();
    const un = subscribeTopLayer(() => {
      // События самого открытия листа пропускаем; следующая смена верха = лист закрыт.
      if (Date.now() - openedAt < 600) return;
      setInfoOpen(false);
      stickRef.current = true;
      // Низ встаёт на запомненную высоту поля СРАЗУ, тем же кадром, что возвращается само
      // поле: ждать первого composerResize от натива — это и есть видимое дёрганье
      // (владелец 2026-08-06). Клавиатуры после карточки нет — в низ идёт только поле.
      snapInsetRef.current(restRef.current);
    });
    return un;
  }, [infoOpen, infoNative]);

  /** Писать нельзя: окно ответа Инстаграма закрыто — та же мерка, что у карточки
   *  собеседника (replyWindowShut): срока нет вовсе — значит закрыто. */
  const commentReply = dialog?.commentReply ?? row.commentReply ?? null;
  /** Окно канала закрыто, но есть право на ОДНО личное сообщение по свежему комментарию
   *  (09.09.2026): поле открыто, над ним подпись, уходит только текст. */
  const commentMode =
    replyWindowShut(channel, dialog?.windowUntil ?? row.windowUntil) && commentReplyOpen(commentReply);
  const windowShut = !canWrite(channel, dialog?.windowUntil ?? row.windowUntil, commentReply);
  /**
   * ПОЧЕМУ НЕЛЬЗЯ ПИСАТЬ — одна строка на все среды (веб, приложение, родной экран).
   * Порядок важен: отключённый аккаунт сильнее окна — при нём не поможет даже свежее
   * сообщение человека. Пусто — писать можно.
   */
  // Замороженный срез (ТЗ ACCOUNTS-TOGGLE §3.7) сильнее всего: аккаунт подключён в другом кабинете.
  const blockedWhy = (dialog?.frozen ?? row.frozen)
    ? t('frozenNote')
    : accountLive === false
    ? t('blockedAccount')
    : (dialog?.peerBlocked ?? row.peerBlocked)
      ? t('blockedByPeer')
      : windowShut
        ? t('blockedWindow')
        : '';
  // Вид родного поля: плейсхолдер, состояние кнопок, тема и акцент кабинета.
  // ПОЛЯ НЕТ ПРИ ЛЮБОЙ ПРИЧИНЕ ЗАПРЕТА (владелец 2026-08-08, IMG_1518: у отключённого
  // аккаунта строка «писать нельзя» стояла, а родное поле оставалось на месте): раньше
  // здесь считалось только окно ответа Инстаграма, отключённый аккаунт и блокировка
  // человеком мимо. Условие ОДНО с вебом — blockedWhy.
  const nativeHidden = composerOn && !!blockedWhy;
  useEffect(() => {
    if (!composerOn) return;
    if (nativeHidden || infoOpen) {
      void nativeBridge()?.clearComposer?.();
      return;
    }
    void nativeBridge()?.configureComposer?.({
      placeholder: t('composerPlaceholder'),
      state: composerState,
      transcribing: '',
      theme: document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light',
      attach: true,
      ...readAccentRgb(),
    });
  }, [composerOn, nativeHidden, infoOpen, composerState, themeTick, t]);
  // Текст в родное поле — только программной сменой (натив владеет текстом при печати и
  // дедупит равное значение, иначе каретка прыгала бы на каждый ввод).
  useEffect(() => {
    if (!composerOn) return;
    void nativeBridge()?.setComposerText?.({ text });
  }, [composerOn, text]);

  // Карточка собеседника (владелец 05.08): сперва СПРЯТАТЬ клавиатуру — веб-поле
  // отпускаем blur-ом, РОДНОЕ поле снимаем clearComposer-ом (иначе оно и клавиатура,
  // нативные вьюхи, рисуются ПОВЕРХ листа, IMG_1293), — потом лист стопкой.
  const openInfo = useCallback(() => {
    (document.activeElement as HTMLElement | null)?.blur?.();
    // Клавиатура уходит — лента ОСТАЁТСЯ на месте (владелец 2026-08-06: «закрываю
    // карточку — всё дёргается»). Прилипание держит низ весь ход клавиатуры вниз и
    // обратно, поэтому переписка не прыгает ни при открытии карточки, ни при закрытии.
    stickRef.current = true;
    toEnd();
    if (nativeComposer) setInfoOpen(true);
    // РОДНАЯ КАРТОЧКА (волна 2, Опус-В2Г): в свежей сборке её рисует натив — веб-лист
    // тогда не поднимаем вовсе (второй карточки на экране не бывает). Данные и слова
    // те же самые, их собирает эффект ниже.
    if (nativeInfoCard) {
      setInfoNative(true);
      return;
    }
    infoPanel.open({
      // Без заголовка «О собеседнике» (владелец 05.08, видео): его место занимает
      // КРУПНЫЙ hero-аватар; лист выше — карточка целиком, не обрезается.
      title: '',
      sheet: true,
      // Телефон в браузере (ТЗ WEB-PARITY-3 Ч27, кадр iOS C09): карточка — лист с «×»; колода под любым
      // листом — закон хоста (drawer.tsx, ТЗ WEB-CAUSES М87/М92).
      // ПРОФИЛЬ ИЗ ПЕРЕПИСКИ — СТОПКОЙ С «×» И БЕЗ ССЫЛКИ НА ПЕРЕПИСКУ (APP-CLAIMS-1020 ред. 11, Д9 / 10.11; слово
      // владельца 21.10.2026: «сообщение — горизонтальной шторкой, контакт — вертикальной стопкой, и там нет ссылки
      // на сообщение»; страница сервера thread.ts `info` → `contact/<id>?inside=1`, present deck). Кружка переписки
      // в шапке нет — карточка и так открыта из неё (`inside`). Имя уровня — чтобы лист лёг поверх бокового слоя
      // переписки стопкой, а не одиночным листом.
      deck: true,
      lead: 'close',
      winKey: 'chat-info',
      // СТОПКА ДО КОНЦА (слово владельца 04.09.2026, паспорт §3.4): лист снизу во всю
      // высоту, верхняя кромка сразу под вырезом, ручка сверху. Прежний детент 0.75
      // оставлял карточку на полвысоты — «у человека будет больше информации, весь экран
      // уместен». Штатный лист без детента = набор EDITOR_SHEET канона NATIVE-SLIDE §11
      // (в приложении под ним экран уходит назад колодой — это и есть стопка).
            body: (
        <DialogInfoSheet
          row={{
            ...row,
            name: dialog?.name ?? row.name,
            username: dialog?.username ?? row.username,
            avatarUrl: dialog?.avatarUrl ?? row.avatarUrl,
            avatarFullUrl: dialog?.avatarFullUrl ?? row.avatarFullUrl,
            windowUntil: dialog?.windowUntil ?? row.windowUntil,
          }}
          account={account}
          mine={{ on: takenOver, toggle: toggleMine }}
          // Метки человека прямо из переписки (П7 обхода владельца 11.09.2026): карточки
          // может ещё не быть — первая метка заведёт её по этой переписке.
          contact={dialog?.contact ?? null}
          onContactSaved={reload}
        />
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- toggleMine стабилен по смыслу
  }, [nativeComposer, nativeInfoCard, infoPanel, t, row, dialog, account, takenOver, toEnd]);
  // «i» нативной шапки слоя (узкий экран): messages.tsx шлёт событие — открываем ту же
  // карточку отсюда (только здесь есть родное поле и замок «Выключить автоматизации»).
  useEffect(() => {
    const h = (e: Event) => {
      if ((e as CustomEvent<{ id?: string }>).detail?.id === row.id) openInfo();
    };
    window.addEventListener('scr-msg-info', h);
    return () => window.removeEventListener('scr-msg-info', h);
  }, [row.id, openInfo]);

  // ── Родной экран переписки: мост (трек Н) ─────────────────────────────────────────
  // Сборка нужна вся: chatSupported — маркер сборки, chatOpen/chatFeed — сам экран.
  // Феатур-детект по PluginHeaders (nativeMethod), НЕ по свойству — капкан Proxy.
  // Сам признак посчитан ВЫШЕ, рядом с родным полем (оно от него зависит, Ф12).
  // ИМЯ В ШАПКЕ — КОРОТКОЕ, то же самое, что в списке чатов (паспорт §3.5): одно правило
  // на весь раздел живёт в lib/display-name.ts. Полное имя видно в карточке «i».
  const headFull = dialog?.name ?? row.name;
  const headNick = dialog?.username ?? row.username;
  const headName = shortName(headFull, headNick ? `@${headNick}` : '') || t('noName');
  // Все слова родной ленты — из словаря кабинета; меняется язык — приезжают заново.
  const words = useChatWords(t, locale, scenarioName);
  /**
   * ТЕ ЖЕ СЛОВА ДЛЯ ВЕБ-ПОКАЗА, но с ОТНОСИТЕЛЬНЫМИ адресами. Родной ленте адреса нужны
   * полными (натив грузит файлы своим запросом), а веб-просмотр ищет нажатый кадр в списке
   * медиа переписки ПО АДРЕСУ — и список собран относительными. Полный адрес в карточке
   * «на что это» не совпал бы с ним, и просмотр открывал бы одну картинку вместо всей
   * переписки (тот же капкан, что у nativeChatMedia, только с другой стороны).
   */
  const webWords = useMemo<NativeChatWords>(() => ({ ...words, origin: '' }), [words]);
  const dict = useMemo<NativeChatDict>(
    () => ({
      back: tRoot('back'),
      info: t('infoTitle'),
      older: t('showOlder'),
      loading: t('loading'),
      empty: t('noText'),
      toLatest: t('toLatest'),
      windowClosed: t('windowClosedHint'),
      mineNote: t('mineNote'),
      mineBack: t('mineBack'),
      play: t('voicePlay'),
      pause: t('voicePause'),
    }),
    [t, tRoot],
  );
  const olderRef = useRef(loadOlder);
  olderRef.current = loadOlder;

  /**
   * БЕСКОНЕЧНАЯ ЛЕНТА ВВЕРХ (Ф10.12, владелец 18.08: кнопку «Показать раньше» убрать —
   * старое подгружается само, как в мессенджерах). Пачка просится, когда до верха ленты
   * осталось меньше ДВУХ экранов: человек не должен успеть увидеть край.
   *
   * Прыжка нет и не может быть: вставку сверху удерживает прежний якорь (anchorRef +
   * useLayoutEffect выше) — тот же, что держал кнопку. Здесь только повод для запроса.
   *
   * Проверка стоит и на подписке, а не только на прокрутке: переписка короче двух экранов
   * не даёт события scroll вовсе, а следующая пачка ей нужна ровно так же.
   * Родного экрана приложения это не касается — там лентой владеет натив (событие
   * chatOlder), веб не крутит ничего.
   */
  useEffect(() => {
    if (nativeChat || nativeScreen || !hasOlder || busy) return;
    const near = (): void => {
      const box = feedScroller(scrollRef.current, framed);
      const top = box ? box.scrollTop : window.scrollY;
      const view = box ? box.clientHeight : window.innerHeight;
      if (needOlder(top, view)) void olderRef.current();
    };
    near();
    // Событие scroll НЕ всплывает — слушаем в фазе захвата, как и прилипание к низу:
    // один слушатель ловит и документ, и тело шторки, и раму разворота.
    document.addEventListener('scroll', near, true);
    return () => document.removeEventListener('scroll', near, true);
  }, [hasOlder, busy, framed, nativeChat, nativeScreen]);
  const closeRef = useRef<(() => void) | undefined>(onClose);
  closeRef.current = onClose;
  const infoRef = useRef(openInfo);
  infoRef.current = openInfo;
  // Страница «раньше» встаёт в НАЧАЛО ленты — натив обязан удержать взгляд на месте.
  const prependRef = useRef(false);
  /** ЧТО УЖЕ УЕХАЛО В РОДНУЮ ЛЕНТУ (круг 9, Э4.1): id ряда → его json, и id самой
   *  переписки рядом. По этой памяти считается разница, которую везёт `chatPatch`.
   *  Обнуляется там же, где родной экран открывается заново (ниже) и при смене
   *  переписки — иначе патч сослался бы на ряды, которых у натива уже нет. */
  const patchSentRef = useRef<{ id: string; map: Map<string, string> }>({ id: '', map: new Map() });

  useEffect(() => {
    if (!nativeChat) return;
    // Экран открывается заново — хранилище рядов у натива пустое, значит и наша память о
    // посланном пуста: следующий патч уедет полным.
    patchSentRef.current = { id: '', map: new Map() };
    const br = nativeBridge();
    if (!br?.chatOpen) return;
    let dead = false;
    let armed = 0;
    void (async () => {
      // ОТКАЗ РОДНОГО ЭКРАНА НЕ ОСТАВЛЯЕТ ПЕРЕПИСКУ БЕЗ ПОЛЯ (Ф13.6). Ответ моста мы
      // проверяем, а срыв вызова ловим: без этого при ok:false поле вставало поверх
      // экрана, который так и не выехал, а при сорвавшемся вызове человек оставался в
      // переписке, куда нечем писать. Не выехал — взводим поле сразу, как было до Ф12:
      // поле поверх анимации переживаемо, переписка без поля — нет.
      let arrived = true;
      // ПОЛЕ ОТВЕТА — СРАЗУ ЗА КОМАНДОЙ «ОТКРОЙ ЭКРАН», НЕ ПОСЛЕ ВЫЕЗДА (владелец
      // 07.09.2026: «поле должно приезжать со шторкой, без ожидания»). Раньше ждали
      // ответа моста и конца выезда (Ф12) — поле вставало на 430 мс позже экрана (журнал).
      // Снимок списка под экраном натив делает В САМОМ chatOpen, до этого вызова, поэтому
      // в снимок поле не попадает; родившись во время выезда, оно цепляется к аниматору
      // карточки и въезжает вместе с ней (ensureComposer в оболочке). Старые сборки без
      // этой доводки показывают поле, как показывали, — хуже прежнего не становится.
      setChatUpFor(row.id);
      try {
        const res: unknown = await br.chatOpen!({
          dialogId: row.id,
          title: headName,
          subtitle: headNick ? `@${headNick}` : '',
          // Фото собеседника в шапку родного экрана (О4.4): тот же адрес, что берёт
          // карточка «i». Поле у моста давно объявлено и необязательно — сборка без него
          // показывает значок человека, как раньше (контракт заморожен, запрет №8).
          avatarUrl: dialog?.avatarUrl ?? row.avatarUrl ?? '',
          theme: document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light',
          dict,
          ...readAccentRgb(),
          // Цвета пузырей — ролями палитры кабинета (владелец 31.08.2026): своей палитры
          // у ленты в Swift нет, тема и набор живые.
          bubbles: readBubbleColors(),
        });
        // Старые сборки не отвечают ничем — это не отказ; отказ — только явное ok:false.
        arrived = !(res && typeof res === 'object' && (res as { ok?: boolean }).ok === false);
      } catch {
        arrived = false;
      }
      if (dead || !arrived) return;
      // Поле уже взведено выше; страховка на случай, если состояние сбросили за время
      // выезда: после его конца взводим ещё раз (идемпотентно).
      armed = window.setTimeout(() => {
        if (!dead) setChatUpFor(row.id);
      }, CHAT_SLIDE_MS);
    })();
    return () => {
      dead = true;
      window.clearTimeout(armed);
      void br.chatClose?.();
    };
    // Экран открывается ОДИН раз на переписку: имя и словарь дальше едут через chatState,
    // иначе смена языка или подъехавшее имя пересоздавали бы экран целиком.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nativeChat, row.id]);

  // Лента: страница пришла — отдаём готовые ряды.
  useEffect(() => {
    if (!nativeChat) return;
    // ПУСТОЙ ПОРЯДОК НЕ УЕЗЖАЕТ ПЕРВЫМ (П39 обхода владельца 12.09.2026: «переписка
    // выезжает пустой, сообщения появляются, когда она уже встала»). Эффект срабатывает
    // первым же рендером, до приезда страницы, и родной экран на пустой порядок рисует
    // «без текста» — ровно на всю анимацию выезда. Пока страница грузится, а рядов нет
    // вовсе (в том числе когда своё отправленное уже стоит в `pending`) — молчим:
    // первый патч уедет с лентой. Мерка чистая, проверяется юнитом (msg-feed.ts).
    if (!chatPatchReady(!!data, loading)) return;
    const br = nativeBridge();
    const page = data?.messages ?? [];
    const onPage = new Set(page.map((m) => m.id));
    const all = [...older.filter((m) => !onPage.has(m.id)), ...page, ...pending];
    const more = headMore ?? data?.more ?? false;
    const focus =
      atMessage && !focusSentRef.current && all.some((m) => m.id === atMessage) ? atMessage : '';
    if (focus) focusSentRef.current = true;
    // ИЗМЕНЕНИЯ ВМЕСТО ВСЕЙ ЛЕНТЫ (круг 9, Э4.1 плана §15.6): сборка умеет `chatPatch` —
    // шлём порядок id и только изменившиеся ряды, остальные натив берёт из хранилища.
    // Феатур-детект ТОЛЬКО через nativeMethod: у Proxy Capacitor «есть» любое поле.
    if (br?.chatPatch && nativeMethod('chatPatch')) {
      const rows = nativeChatRows(all, words);
      const seen = patchSentRef.current.id === row.id ? patchSentRef.current.map : new Map();
      const patch = nativeChatPatch(seen, rows);
      patchSentRef.current = { id: row.id, map: patch.next };
      // Догрузка «раньше» едет тем же путём: порядок id выражает вставку сверху сам.
      prependRef.current = false;
      void br.chatPatch({
        dialogId: row.id,
        order: patch.order,
        rows: patch.rows,
        more,
        ...(focus ? { focus } : {}),
      });
      return;
    }
    if (!br?.chatFeed) return;
    const mode = prependRef.current ? 'prepend' : 'reset';
    prependRef.current = false;
    void br.chatFeed({
      dialogId: row.id,
      rows: nativeChatRows(all, words),
      more,
      mode,
      ...(focus ? { focus } : {}),
    });
    // `words` в зависимостях нарочно: язык кабинета меняется НА МЕСТЕ (канон
    // lang-switch) — новые слова обязаны доехать до уже открытой ленты, а не ждать
    // следующего сообщения.
  }, [nativeChat, data, loading, older, pending, headMore, row.id, words]);

  // МЕДИА ПЕРЕПИСКИ ДЛЯ РОДНОГО ПРОСМОТРА (волна 3): нажали кадр в родной ленте — натив
  // открывает свой просмотр и листает ИМЕННО ЭТОТ список, тот же самый, по которому
  // листает веб-слой (общая рука feedMedia). Едет вместе с лентой: страница пришла —
  // список освежился. Сборка метода не знает — вызов молчит, как было до волны 3.
  /** Что уже уехало нативу: список пересобирается КАЖДЫМ рендером живой переписки
   *  (опрос, набор текста), а шлём только настоящую разницу — правило «патчи не трогают
   *  неизменное». */
  const mediaSentRef = useRef('');
  useEffect(() => {
    if (!nativeChat) return;
    const br = nativeBridge();
    if (!br?.chatMedia) return;
    const data = {
      dialogId: row.id,
      // Адреса ПОЛНЫЕ — как у рядов ленты: по адресу натив ищет нажатый кадр в списке.
      items: nativeChatMedia(messages, words.origin, {
        story: t('kindStory'),
        storyReply: t('replyToStory'),
      }),
      words: { viewer: t('viewer'), openIg: t('openInInstagram') },
    };
    const next = JSON.stringify(data);
    if (next === mediaSentRef.current) return;
    mediaSentRef.current = next;
    void br.chatMedia(data);
  }, [nativeChat, messages, words.origin, row.id, t]);

  // Шапка, замок «отвечаю сам», ошибка отправки и живой словарь.
  useEffect(() => {
    if (!nativeChat) return;
    const br = nativeBridge();
    if (!br?.chatState) return;
    void br.chatState({
      dialogId: row.id,
      title: headName,
      subtitle: headNick ? `@${headNick}` : '',
      mineNote: takenOver ? t('mineNote') : '',
      error: err,
      canReply: !blockedWhy,
      // Почему нельзя писать — словами, у самого поля ответа.
      blockedNote: blockedWhy,
      theme: document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light',
      dict,
      ...readAccentRgb(),
      // Тему и набор меняют НА МЕСТЕ — открытая лента перекрашивается этим же вызовом.
      bubbles: readBubbleColors(),
    });
    // themeTick — смена темы/оттенка кабинета (наблюдатель ниже): экран следует КАБИНЕТУ,
    // а не системе, как нижнее меню и родное поле.
  }, [nativeChat, row.id, headName, headNick, takenOver, err, blockedWhy, dict, t, themeTick]);

  // Намерения человека с родного экрана: назад, карточка, «раньше», ссылки, подпись бота.
  useEffect(() => {
    if (!nativeChat) return;
    const br = nativeBridge();
    if (!br?.addListener) return;
    let dead = false;
    const subs: Array<{ remove: () => void | Promise<void> }> = [];
    void (async () => {
      const add = async (
        ev: 'chatBack' | 'chatInfo' | 'chatOlder' | 'chatOpenUrl' | 'chatOpenBot' | 'chatVisible',
        cb: (d: { url?: string; id?: string }) => void,
      ): Promise<void> => {
        const sub = await br.addListener!(ev, cb);
        if (dead) void sub.remove();
        else subs.push(sub);
      };
      await add('chatBack', () => closeRef.current?.());
      await add('chatInfo', () => infoRef.current());
      await add('chatOlder', () => void olderRef.current());
      await add('chatVisible', () => reload());
      await add('chatOpenUrl', (d) => {
        if (d?.url) window.open(d.url, '_blank');
      });
      await add('chatOpenBot', (d) => {
        if (d?.id) router.push(`/app/bots/${d.id}`);
      });
    })();
    return () => {
      dead = true;
      for (const sub of subs) void sub.remove();
    };
  }, [nativeChat, reload, router]);

  // ── РОДНАЯ КАРТОЧКА СОБЕСЕДНИКА (волна 2, Опус-В2Г) ───────────────────────────────
  // Лист рисует натив, мозг остаётся здесь: те же слова, тот же остаток окна ответа, тот
  // же замок диалога и то же освежение профиля, что у веб-карточки (DialogInfoSheet).
  // Фото собеседника готовит ВЕБ (imgSquarePng — общий конвейер картинок для натива):
  // клиент ничего не качает сам.
  const [infoPhoto, setInfoPhoto] = useState('');
  const infoAvatar = dialog?.avatarUrl ?? row.avatarUrl ?? '';
  // ПОЛНАЯ копия — для просмотра по тапу (владелец 09.09.2026); её нет (записи до этого
  // дня) — открывается маленькая, нет и её — натив тапа не вешает (правило п.4).
  const infoAvatarFull = dialog?.avatarFullUrl || row.avatarFullUrl || infoAvatar;
  useEffect(() => {
    if (!infoNative || !infoAvatar) return;
    let dead = false;
    void imgSquarePng(infoAvatar, 320).then((data) => {
      if (!dead) setInfoPhoto(data ?? '');
    });
    return () => {
      dead = true;
    };
  }, [infoNative, infoAvatar]);
  // Открыли карточку — просим у сервера свежий профиль (имя, логин, аватар могли
  // смениться): ровно то же делает веб-карточка при открытии. Приехал ответ — перечитываем
  // переписку своей же рукой, и новые значения ложатся в лист патчем.
  useEffect(() => {
    if (!infoNative) return;
    let alive = true;
    void fetch(`/api/cabinet/messages/${row.id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refreshProfile: true }),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { ok?: boolean; profile?: unknown } | null) => {
        if (alive && j?.ok && j.profile) reload();
      })
      .catch(() => null);
    return () => {
      alive = false;
    };
  }, [infoNative, row.id, reload]);
  // Остаток окна живой: карточку держат открытой, а цифра со временем врала бы. Тик
  // просто перерисовывает переписку — строку остатка считает сам рендер.
  const [, setInfoTick] = useState(0);
  useEffect(() => {
    if (!infoNative) return;
    const id = setInterval(() => setInfoTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, [infoNative]);
  /** Тёмная ли тема кабинета — реактивно и БЕЗ чтения документа в рендере (SSR). */
  const infoDark = useIsDark();
  const infoUntil = dialog?.windowUntil ?? row.windowUntil;
  // Та же мерка, что у поля ответа: срока нет вовсе — окно закрыто (О23.3). Остаток
  // считаем В РЕНДЕРЕ: тик выше перерисовывает карточку, и новая строка приезжает сама.
  const infoClosed = !canWrite(channel, infoUntil, commentReply);
  const infoWindowValue =
    channel === 'tg'
      ? t('infoWindowFree')
      : infoClosed
        ? t('windowClosed')
        : windowLeft(infoUntil, t('hoursShort'), t('minutesShort'));
  /** Убрать родную карточку и вернуть поле ответа: одна рука на уход листа (свайп,
   *  крестик) и на удаление человека из самой карточки. */
  const dropInfoRef = useRef<() => void>(() => {});
  /** Чья карточка сейчас открыта: чужие события листа отсекаем по этому ключу. */
  const rowIdRef = useRef(row.id);
  rowIdRef.current = row.id;
  // КАРТОЧКА СОБЕСЕДНИКА = КАРТОЧКА КОНТАКТА (владелец 09.09.2026): лист один на «Чаты»
  // и «Контакты», и собирает его ОДНА рука — `useContactCardNative` раздела «Контакты».
  // Слова переписки (сколько осталось на ответ, канал, аккаунт, кто отвечает) считает
  // по-прежнему сама переписка и отдаёт их готовыми; факты человека, метки, переменные и
  // кнопки «Изменить»/«Удалить» рука берёт из карточки контакта (её присылает ручка).
  const infoCard = useContactCardNative({
    contact: dialog?.contact ?? null,
    // КАРТОЧКИ У ПЕРЕПИСКИ МОЖЕТ НЕ БЫТЬ ВОВСЕ (П7 обхода владельца 11.09.2026): метку
    // тогда записывает ручка кабинета ПО ПЕРЕПИСКЕ — она же заводит карточку человека.
    dialogId: row.id,
    onSaved: reload,
    onDeleted: () => {
      dropInfoRef.current();
      reload();
    },
  });
  const infoData: NativeChatInfoData = infoCard.build({
    cardId: row.id,
    dark: infoDark,
    account: account ?? '',
    hero: {
      title: headName,
      // Полное имя — второй строкой карточки, если оно отличается от короткого (§3.5);
      // логин остаётся там же, через точку.
      meta: [fullNameNote(headFull, headName), headNick ? `@${headNick}` : '']
        .filter(Boolean)
        .join(' · '),
      photo: infoPhoto,
      // Адрес фото в полный размер (владелец 09.09.2026): тап по аватарке в родной
      // карточке открывает его тем же просмотром, что кадр публикации. Ключ ДОБАВОЧНЫЙ —
      // старые сборки его не читают (запрет №8: имя и форма метода прежние).
      photoUrl: infoAvatarFull,
      initial: contactInitial(headName),
    },
    dialog: {
      id: row.id,
      // ЗНАЧОК СЕТИ ПЕРЕД СЛОВОМ — как в веб-карточке (.msg-info-val: ChannelIcon 14 и
      // следом «Telegram»/«Instagram»). Картинка — каталога сборки, теми же именами,
      // что у рядов «Подключений» (аудит халявы 01.09).
      channelImage: channel === 'tg' ? 'ChanTG' : 'ChanIG',
      channelValue: channel === 'tg' ? 'Telegram' : 'Instagram',
      windowValue: infoWindowValue,
      // Замок диалога — строка-действие: тап уходит вебу (chatInfoTap), а спрашивает и
      // переключает он сам — теми же словами и тем же вопросом, что на веб-карточке.
      // СЛОВА КОРОТКИЕ (владелец 09.09.2026, ТЗ «Контакты» п.4): в карточке строка
      // читается «Отвечает — Бот» / «Отвечает — Вы». Длинные «Автоматизации выключены» и
      // «Выключить автоматизации» остаются на веб-карточке и на кнопке в переписке.
      mineValue: takenOver ? t('answerYou') : t('answerBot'),
      hint:
        channel === 'ig'
          ? infoClosed
            ? t('windowClosedHint')
            : commentMode
              ? t('commentReplyHint')
              : t('infoWindowHint')
          : '',
      // Карточку открыли ИЗ переписки — вести в неё же незачем.
      inside: true,
    },
  });
  // Руки карточки — рефами: подписка на события листа живёт одна, а разбирать намерение
  // обязана свежая рука (в ней и правка, и подтверждение удаления).
  const infoTapRef = useRef(infoCard.tap);
  infoTapRef.current = infoCard.tap;
  const infoSetRef = useRef(infoCard.set);
  infoSetRef.current = infoCard.set;
  const infoResetRef = useRef(infoCard.reset);
  infoResetRef.current = infoCard.reset;
  dropInfoRef.current = () => {
    // Правка и подтверждение удаления живут ровно столько, сколько открытая карточка.
    infoResetRef.current();
    setInfoNative(false);
    setInfoOpen(false);
    stickRef.current = true;
    // Низ встаёт на запомненную высоту поля тем же кадром, что возвращается поле —
    // ждать первого composerResize нельзя, это и есть видимое дёрганье.
    snapInsetRef.current(restRef.current);
  };

  /** Что уже уехало нативу: патч шлём только на настоящую разницу (правило «патчи не
   *  трогают неизменное»), а не на каждый ререндер живой переписки. */
  const infoSentRef = useRef('');
  useEffect(() => {
    if (!infoNative) return;
    const br = nativeBridge();
    if (!br?.chatInfoOpen) return;
    void br.chatInfoOpen(infoData);
    infoSentRef.current = JSON.stringify(infoData);
    return () => {
      infoSentRef.current = '';
      void nativeBridge()?.chatInfoClose?.();
    };
    // Открываем ОДИН раз на карточку: свежий профиль и живой остаток окна едут патчем.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infoNative]);
  useEffect(() => {
    if (!infoNative || !infoSentRef.current) return;
    const next = JSON.stringify(infoData);
    if (next === infoSentRef.current) return;
    infoSentRef.current = next;
    void nativeBridge()?.chatInfoPatch?.(infoData);
  });
  // Замок диалога с родной карточки: вопрос и переключение — ТЕ ЖЕ, что на веб-карточке
  // (MineToggle). Рука — рефом: подписка живёт одна, а значения (кто отвечает сейчас)
  // обязаны быть свежими.
  const askMineRef = useRef<() => void>(() => {});
  askMineRef.current = () => {
    // Возврат боту вопросов не задаёт (как и на веб-карточке) — вопрос только у выключения.
    if (takenOver || !hasNativeConfirm()) {
      void toggleMine();
      return;
    }
    void askNativeConfirm({
      question: t('mineAsk'),
      confirmLabel: t('mineAskYes'),
      cancelLabel: tRoot('cancel'),
      destructive: false,
    }).then((yes) => {
      if (yes) void toggleMine();
    });
  };
  useEffect(() => {
    if (!nativeInfoCard) return;
    const br = nativeBridge();
    if (!br?.addListener) return;
    let dead = false;
    const subs: Array<{ remove: () => void | Promise<void> }> = [];
    void (async () => {
      const add = async (
        ev: 'chatInfoTap' | 'chatInfoGone' | 'chatInfoSet',
        cb: (d: { key?: string; value?: string | boolean | string[]; dialogId?: string }) => void,
      ): Promise<void> => {
        const sub = await br.addListener!(ev, cb);
        if (dead) void sub.remove();
        else subs.push(sub);
      };
      // Лист один на «Чаты» и «Контакты»: чужое событие (карточка человека, открытая с
      // «Контактов») отсекаем по ключу карточки — у нас это id переписки.
      const mine = (d: { dialogId?: string }) => !d?.dialogId || d.dialogId === rowIdRef.current;
      await add('chatInfoTap', (d) => {
        if (!mine(d)) return;
        if (d?.key === 'mine') askMineRef.current();
        // Остальные строки («Изменить», «Удалить», правка) разбирает общая рука карточки.
        else infoTapRef.current(d?.key ?? '');
      });
      // Правка поля карточки родным листом: та же рука, что и в «Контактах».
      await add('chatInfoSet', (d) => {
        if (mine(d) && typeof d?.value === 'string') infoSetRef.current(d?.key ?? '', d.value);
      });
      await add('chatInfoGone', (d) => {
        if (!mine(d)) return;
        dropInfoRef.current();
      });
    })();
    return () => {
      dead = true;
      for (const sub of subs) void sub.remove();
    };
  }, [nativeInfoCard]);

  // ── РОДНОЙ ВЫБОР ФАЙЛА: выбранный файл приезжает сюда ─────────────────────────────
  // Дальше ВСЁ РОВНО ТАК ЖЕ, как при веб-выборе: та же проверка правил канала, те же
  // слова отказа на своём месте, та же заливка, та же отправка (onPickFile) и та же
  // подпись — текст поля ответа. Своей подписи натив больше не присылает: листа-превью
  // у него нет (владелец 01.09). Файл, который не проходит мостом, натив не берёт вовсе —
  // тогда открываем свой прежний ввод, где байты никуда не переезжают.
  const filePickRef = useRef<(name: string, mime: string, data: string) => void>(() => {});
  filePickRef.current = (name, mime, data) => {
    void (async () => {
      const blob = await fetch(data)
        .then((r) => r.blob())
        .catch(() => null);
      if (!blob) {
        setErr(t('err_send'));
        return;
      }
      await onPickFile(new File([blob], name, { type: mime || blob.type }));
    })();
  };
  useEffect(() => {
    if (!nativeAttach) return;
    const br = nativeBridge();
    if (!br?.addListener) return;
    let dead = false;
    const subs: Array<{ remove: () => void | Promise<void> }> = [];
    void (async () => {
      const add = async (
        ev: 'chatFile' | 'chatFileHeavy',
        cb: (d: { name?: string; mime?: string; data?: string }) => void,
      ): Promise<void> => {
        const sub = await br.addListener!(ev, cb);
        if (dead) void sub.remove();
        else subs.push(sub);
      };
      await add('chatFile', (d) => {
        if (!d?.data) return;
        filePickRef.current(d.name ?? 'file', d.mime ?? '', d.data);
      });
      await add('chatFileHeavy', () => fileRef.current?.click());
    })();
    return () => {
      dead = true;
      for (const sub of subs) void sub.remove();
    };
  }, [nativeAttach]);

  // ── РОДНОЙ ЭКРАН ПЕРЕПИСКИ В ПРИЛОЖЕНИИ (трек Н) ──────────────────────────────────
  // Ленту рисует и крутит натив, а мозг остаётся здесь: те же запросы, те же правила
  // каналов, тот же словарь. Рядов натив не собирает — они приезжают готовыми
  // (lib/msg-feed.ts): язык кабинета меняется на месте, в Swift строк нет.
  if (nativeChat) {
    return (
      <>
        {/* Выбор файла нужен и в родном режиме: скрепку рисует натив, а файл выбирает
            и шлёт веб — он один знает правила канала. */}
        <KInput
          ref={fileRef}
          type="file"
          hidden
          accept={
            CHAT_ATTACH_KINDS.flatMap((k) => chatAttachLimit(chan, k)?.accept ?? []).length > 0 &&
            chan !== 'telegram'
              ? CHAT_ATTACH_KINDS.flatMap((k) => chatAttachLimit(chan, k)?.accept ?? []).join(',')
              : undefined
          }
          onChange={(e) => {
            void onPickFile(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      </>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} aria-busy="true">
        <Sk w={200} h={13} />
        <Sk w={260} h={40} />
        <Sk w={180} h={40} />
      </div>
    );
  }
  if (!data && error) return <LoadError retry={reload} />;

  const accepts = CHAT_ATTACH_KINDS.flatMap((k) => chatAttachLimit(chan, k)?.accept ?? []);
  const attachAccept = accepts.length > 0 && chan !== 'telegram' ? accepts.join(',') : undefined;
  // ВЫБОР «ФОТО / ФАЙЛ» — КАК РОДНОЕ МЕНЮ ДЕЙСТВИЙ iOS (кадр C07-chat-attach, сверка П12 03.10):
  // стеклянная карточка 240 у низа экрана, заголовок «Приложить файл» по центру и две серые
  // капсулы столбиком — «Фото» сверху, «Файл» под ней; «Отмены» нет, закрывает тап мимо.
  // Раньше был полулист со строками (KPickSheet). Окно набора KDialog, вид — .k-dlg--attach.
  const pickAttach = (v: 'photo' | 'file') => {
    setAttachPick(false);
    const el = fileRef.current;
    if (!el) return;
    el.accept = v === 'photo' ? 'image/*,video/*' : (attachAccept ?? '');
    el.click();
  };
  const attachPickSheet = attachPick ? (
    <KDialog
      open
      onOpenChange={(v) => {
        if (!v) setAttachPick(false);
      }}
      title={t('attach')}
      className="k-dlg--ask k-dlg--stack k-dlg--attach"
      footer={
        <>
          {/* Столбик идёт снизу вверх (k-dlg--stack): «Файл» первым в разметке — внизу. */}
          <KButton onClick={() => pickAttach('file')}>{t('kindFile')}</KButton>
          <KButton onClick={() => pickAttach('photo')}>{t('kindImage')}</KButton>
        </>
      }
    >
      {null}
    </KDialog>
  ) : null;

  // Кнопка «к последнему» — та же, что в чате ассистента (.scn-jump над полем ввода).
  const jumpBtn = (
    <KBareButton
      className={`scn-jump${showJump ? ' show' : ''}`}
      aria-label={t('toLatest')}
      onMouseDown={(e) => e.preventDefault()}
      onClick={jumpToBottom}
      aria-hidden={!showJump}
      tabIndex={showJump ? 0 : -1}
    >
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden>
        <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </KBareButton>
  );

  const headTitle = (dialog?.name ?? row.name) || (dialog?.username ?? row.username) || t('noName');

  // ВКЛЮЧЁННЫЙ РЕЖИМ ВИДНО ВСЕГДА (владелец 2026-08-07): пока диалог у человека, бот в
  // нём молчит — об этом говорит строка-предупреждение с действием «вернуть боту» рядом.
  // Прежде о режиме сообщало только слово в углу да серая строка события в ленте, и
  // владелец несколько раз «молча терял» бота. Строка стоит в строке состояния переписки
  // (закреплённой у верха) — значит видна на любой прокрутке, а не только сверху ленты.
  const mineNote = takenOver ? (
    <div className="msg-dlg-mnote">
      <span>{t('mineNote')}</span>
      <KBareButton className="msg-dlg-mine on" onClick={toggleMine}>
        {t('mineBack')}
      </KBareButton>
    </div>
  ) : null;

  return (
    <div
      className={`${framed ? 'chat scn-chat msg-dlg msg-dlg-framed' : 'msg-dlg'}${takenOver ? ' msg-mine-on' : ''}`}
    >
      {/* СТЕКЛО НАД ЛЕНТОЙ (§12.1 п.1): полоса ростом с шапку слоя плюс 40, набранная
          лентами растущего размытия. В раме (вторая колонка десктопа) её нет — там лента
          под шапку не заезжает; на широком экране полосу гасит сам cabinet.css. */}
      {framed ? null : <GlassStrip />}
      {framed ? (
        // Шапка «как на iPhone» (владелец 05.08 вместо прежней каши): круглая «назад»
        // слева · имя собеседника по центру · «i» справа. Логин, окно ответа и замок
        // «Выключить автоматизации» переехали в карточку собеседника («i»).
        <div className="msg-dlg-head">
          {back ? (
            <KBareButton className="dr-close msg-dlg-hbtn" onClick={back} aria-label={tRoot('back')} title={tRoot('back')}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </KBareButton>
          ) : (
            <span className="dr-close msg-dlg-hbtn hidden" aria-hidden />
          )}
          {/* ИМЯ И ЛОГИН ПОД НИМ, СПРАВА — ФОТО СОБЕСЕДНИКА (партия Н5, кадр iOS C05-chat-open):
              шапка поля разворота = шапка листа переписки на телефоне (messages.tsx,
              headerRight): тап по фото открывает карточку собеседника. Голой «i» больше нет. */}
          <div className="msg-dlg-head-t">
            {headTitle}
            {row.username ? <span className="msg-dlg-head-sub">@{row.username}</span> : null}
          </div>
          <KBareButton className="dr-close msg-dlg-hbtn dr-info msg-dlg-ava" onClick={openInfo} aria-label={t('infoTitle')} title={t('infoTitle')}>
            <DialogAvatar dialog={row} />
          </KBareButton>
          {mineNote}
        </div>
      ) : null}
      {/* ПЛАШКИ НАД ЛЕНТОЙ НЕТ (владелец 2026-08-09, IMG_1522): полоса с логином,
          словами «Окно ответа закрыто» и кнопкой «Выключить автоматизации» висела
          посреди экрана и мешала читать. Логин теперь строго под именем — второй
          строкой шапки слоя (messages.tsx, subtitle); почему нельзя писать — строкой
          внизу на месте поля; выключение автоматизаций — в карточке собеседника («i»),
          а состояние видно статусом в списке переписок. */}

      {/* СТРОКИ ЗАГРУЗКИ ПЕРЕПИСКИ ЗДЕСЬ БОЛЬШЕ НЕТ (правило 1 владельца 11.09.2026):
          «Скачиваем… N», «Обновляем…» и «Обновлено в 10:14» сняты во всём продукте —
          и под шапкой переписки, и под заголовком раздела, и в приложении. Поле `sync`
          в ответе ручки осталось (его считает сервер), но человеку оно не показывается.
          Возвращать строку на экран запрещено. */}

      <div
        className={framed ? 'chat-scroll msg-feed' : 'msg-feed'}
        ref={scrollRef}
        onScroll={framed ? onFeedScroll : undefined}
      >
        {/* Кнопки «Показать раньше» здесь БОЛЬШЕ НЕТ (Ф10.12, владелец 18.08): старое
            подгружается само за два экрана до верха. Слово осталось только у родной ленты
            приложения — там «раньше» просит натив своим событием. */}
        {rows.map(({ m, album }, idx) => (
          <Fragment key={m.id}>
            {/* Дата-разделитель при смене суток — как в чате ассистента (владелец 2026-07-23). */}
            {dayKey(m.at) !== dayKey(rows[idx - 1]?.m.at) && (
              <div className="chat-date">
                <span>{fmtMsgDate(m.at, locale)}</span>
              </div>
            )}
            {/* НАЙДЕННАЯ РЕПЛИКА (О22.5): свой ряд не заводим — тот же пузырь получает
                метку подсветки и ссылку, по которой лента к нему доезжает. Обёртки нет
                нарочно: лишний слой в ленте сдвинул бы отступы всех соседей. */}
            {markHit(renderMessage(m, album, groups[idx]), m.id)}
          </Fragment>
        ))}
        <div ref={endRef} />
      </div>

      {/* канон: UI-TEMPLATE-CANON «Ошибка всего окна» (П128) — одна красная строка окна
          компонентом набора; своего класса у переписки больше нет. Стоит у поля ввода:
          она про попытку отправить, а не про правило формы. */}
      {err && <FFormError flush>{err}</FFormError>}

      {/* Выбор файла живёт ВНЕ композера: в приложении веб-композер спрятан (поле рисует
          натив), а скрепка родного поля дёргает этот же input — из скрытого поддерева он
          бы не открылся. */}
      {attachPickSheet}
      <KInput
        ref={fileRef}
        type="file"
        hidden
        accept={attachAccept}
        onChange={(e) => {
          void onPickFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />

      {/* msg-dlg-composer остаётся и в раме: по нему сборка приложения прячет веб-поле,
          когда поле рисует натив (широкий экран бывает и в приложении — iPad). */}
      {/* ПЛАШКА СТОИТ В ПОТОКЕ, А НЕ ПОВЕРХ ЛЕНТЫ (Ф10.15): в развороте она была вынута
          из потока и накрывала последние пузыри — запаса ленты (116px) хватало только
          на поле ответа, а у плашки текст в 2–3 строки. Правило — в cabinet.css. */}
      {blockedWhy ? (
        // На телефоне плашка — сама стекло-панель (роль .k-glass-bar, П3); в раме — карточка.
        <div className={framed ? 'card msg-dlg-closed' : 'msg-dlg-closed k-glass-bar'} ref={bottomRef}>
          <div className="meta" style={{ lineHeight: 1.5 }}>{blockedWhy}</div>
        </div>
      ) : (
        <div className={framed ? 'chat-composer msg-dlg-composer' : 'msg-dlg-composer'} ref={bottomRef}>
          {framed && jumpBtn}
          {commentMode ? (
            <div className="meta" style={{ lineHeight: 1.5, padding: '0 4px 6px' }}>{t('commentReplyNote')}</div>
          ) : null}
          <Composer
            stack
            value={text}
            onChange={setText}
            onSubmit={() => void send({ kind: 'text', text: text.trim() })}
            busy={busy}
            left={
              <KBareButton
                className="msg-clip"
                onClick={() => attachRef.current()}
                disabled={busy}
                aria-label={t('attach')}
                title={t('attach')}
              >
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M21 11.5 12.5 20a5 5 0 0 1-7-7l8-8a3.5 3.5 0 1 1 5 5l-8 8a2 2 0 0 1-3-3l7.5-7.5" />
                </svg>
              </KBareButton>
            }
            // Микрофон здесь ЗАПИСЫВАЕТ ГОЛОСОВОЕ, а не диктует текст: распознавание
            // платное, а человеку в переписке уезжает сам звук (М7).
            onVoice={(blob, seconds) => void sendVoice(blob, seconds)}
            dict={{
              placeholder: t('composerPlaceholder'),
              micTranscribing: '',
              micStart: t('voiceStart'),
              micStop: t('voiceStop'),
              micError: t('voiceError'),
              micCancel: t('voiceCancel'),
            }}
          />
        </div>
      )}

    </div>
  );

  /** Пометить найденную реплику: класс подсветки и ссылка для доезда ленты. */
  function markHit(node: ReactNode, id: string): ReactNode {
    if (!atMessage || id !== atMessage || !isValidElement(node)) return node;
    const props = node.props as { className?: string };
    return cloneElement(node as ReactElement<{ className?: string; ref?: unknown }>, {
      className: `${props.className ?? ''} msg-hit`.trim(),
      ref: hitRef,
    });
  }

  /** Одно сообщение ленты — общий показ `renderFeedMessage` (он же рисует письмо рассылки в отчёте, Р2 ред. 7). */
  function renderMessage(m: FeedMessage, album: FeedMessage[] = [m], group?: { first: boolean; last: boolean }) {
    return renderFeedMessage({ t, locale, markId, openMedia, scenarioName, webWords }, m, album, group);
  }
}
